import * as THREE from 'three';
import { InputManager } from './InputManager';
import { ThirdPersonCamera } from './ThirdPersonCamera';
import { Player } from '@entities/Player';
import { Enemy } from '@entities/Enemy';
import { NPC } from '@entities/NPC';
import { LootDrop } from '@entities/LootDrop';
import { RemotePlayer } from '@entities/RemotePlayer';
import { TargetingSystem } from '@systems/TargetingSystem';
import { CombatSystem } from '@systems/CombatSystem';
import { InventorySystem } from '@systems/InventorySystem';
import { QuestSystem } from '@systems/QuestSystem';
import { SkillSystem } from '@systems/SkillSystem';
import { AudioSystem } from '@systems/AudioSystem';
// New performance systems
import { ParticleSystem } from '@systems/ParticleSystem';
import { Quadtree, type SpatialEntity } from '@systems/SpatialPartitioning';
import { ComboSystem, type SkillId } from '@systems/ComboSystem';
import { EnhancementSystem, SocketSystem } from '@systems/EnhancementSystem';
import { LODSystem } from '@systems/LODSystem';
import { SkillEffectCache } from '@systems/SkillEffectCache';
import { WorldChunkManager } from '@systems/WorldChunking';
import { Environment } from '@systems/Environment';
import { NetworkManager } from '@network/NetworkManager';
import { HUD } from '@ui/HUD';
import { DialogUI, type NPCInfo } from '@ui/DialogUI';
import { ChatUI } from '@ui/ChatUI';
import { FloatingTextSystem } from '@ui/FloatingText';
import { CombatLog } from '@ui/CombatLog';
import { NameplateSystem } from '@ui/Nameplates';
import { OptimizedNameplateSystem } from '@ui/OptimizedNameplates';
import { StatsPanel } from '@ui/StatsPanel';
import { Minimap } from '@ui/Minimap';
import { ComboUI } from '@ui/ComboUI';
import { EnhancementUI } from '@ui/EnhancementUI';
import { GAME_CONFIG, ITEMS, VENDORS } from 'shared';
import type { PlayerId, ChatChannel } from 'shared';

export interface GameConfig {
  canvas: HTMLCanvasElement;
  showFPS?: boolean;
  quality?: 'low' | 'medium' | 'high';
}

// PERF: Quality presets for different hardware
const QualitySettings = {
  low: {
    pixelRatio: 1,
    shadowMapSize: 512,
    shadowsEnabled: false,
    antialias: false,
    fogDistance: 100,
    maxEnemyDistance: 60,
  },
  medium: {
    pixelRatio: Math.min(window.devicePixelRatio, 1.5),
    shadowMapSize: 1024,
    shadowsEnabled: true,
    antialias: false,
    fogDistance: 150,
    maxEnemyDistance: 80,
  },
  high: {
    pixelRatio: Math.min(window.devicePixelRatio, 2),
    shadowMapSize: 2048,
    shadowsEnabled: true,
    antialias: true,
    fogDistance: 200,
    maxEnemyDistance: 100,
  },
};

export class Game {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private canvas: HTMLCanvasElement;

  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private lastTime: number = 0;
  private deltaTime: number = 0;

  // FPS tracking
  private frameCount: number = 0;
  private fpsTime: number = 0;
  private currentFPS: number = 0;
  private fpsElement: HTMLElement | null = null;
  private showFPS: boolean;

  // PERF: Shadow update throttling
  private shadowUpdateCounter: number = 0;
  private readonly SHADOW_UPDATE_INTERVAL: number = 3; // Update every 3 frames

  // Core systems
  private inputManager: InputManager;
  private thirdPersonCamera: ThirdPersonCamera;
  private targetingSystem: TargetingSystem;
  private combatSystem: CombatSystem;
  private inventorySystem: InventorySystem;
  private questSystem: QuestSystem;
  private skillSystem: SkillSystem;

  // New performance systems
  private particleSystem!: ParticleSystem;
  private spatialTree!: Quadtree;
  private comboSystem!: ComboSystem;
  private enhancementSystem!: EnhancementSystem;
  private socketSystem!: SocketSystem;
  private lodSystem!: LODSystem;
  private skillEffectCache!: SkillEffectCache;
  private worldChunkManager!: WorldChunkManager;
  private optimizedNameplates!: OptimizedNameplateSystem;

  // UI
  private hud: HUD;
  private dialogUI: DialogUI;
  private chatUI: ChatUI;
  private combatLog: CombatLog;
  private nameplateSystem: NameplateSystem;
  private statsPanel: StatsPanel;
  private minimap: Minimap;
  private comboUI!: ComboUI;
  private enhancementUI!: EnhancementUI;

  // Audio
  private audioSystem: AudioSystem;

  // Auto-attack
  private autoAttackEnabled: boolean = false;
  private lastAutoAttackTime: number = 0;
  private autoAttackInterval: number = 2000; // ms

  // Floating Text
  private floatingText!: FloatingTextSystem;

  // Network
  private networkManager: NetworkManager;
  private remotePlayers: Map<PlayerId, RemotePlayer> = new Map();
  private isMultiplayer: boolean = false;

  // Entities
  private player: Player;
  private enemies: Enemy[] = [];
  private npcs: NPC[] = [];
  private lootDrops: LootDrop[] = [];
  private selectedNPC: NPC | null = null;
  private pendingNPCInteraction: NPC | null = null; // NPC to interact with when player arrives

  // PERF: Bound game loop to avoid per-frame allocation
  private readonly boundGameLoop: (time: number) => void;

  // PERF: Reusable vectors for player position lookups
  private readonly _playerPos: THREE.Vector3 = new THREE.Vector3();

  // PERF: Frustum culling for entities
  private readonly _frustum: THREE.Frustum = new THREE.Frustum();
  private readonly _projScreenMatrix: THREE.Matrix4 = new THREE.Matrix4();

  // Click-to-move raycasting
  private readonly _groundRaycaster: THREE.Raycaster = new THREE.Raycaster();
  private readonly _mouseVec2: THREE.Vector2 = new THREE.Vector2();
  private groundPlane: THREE.Mesh | null = null;

  // Environment system (skybox, ground, atmosphere)
  private environment: Environment | null = null;

  // Track mouse movement to distinguish click from drag
  private mouseDownPos: { x: number; y: number } | null = null;
  private readonly CLICK_THRESHOLD = 5; // pixels - if mouse moves more than this, it's a drag

  // Stats tracking
  private experience: number = 0;
  private level: number = 1;

  constructor(config: GameConfig) {
    this.canvas = config.canvas;
    this.showFPS = config.showFPS ?? true;

    // PERF: Apply quality settings based on config or auto-detect
    const qualityLevel = config.quality ?? this.detectQuality();
    const quality = QualitySettings[qualityLevel];

    // Initialize Three.js renderer with performance optimizations
    this.renderer = new THREE.WebGLRenderer({
      canvas: config.canvas,
      antialias: quality.antialias,
      alpha: false,
      powerPreference: 'high-performance', // PERF: Prefer discrete GPU
      stencil: false, // PERF: Disable stencil buffer if not needed
    });
    this.renderer.setPixelRatio(quality.pixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setClearColor(0x1a1a2e);
    this.renderer.shadowMap.enabled = quality.shadowsEnabled;
    this.renderer.shadowMap.type = quality.shadowsEnabled ? THREE.PCFSoftShadowMap : THREE.BasicShadowMap;
    this.renderer.shadowMap.autoUpdate = false; // PERF: Manual shadow updates

    // Initialize scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x1a1a2e, 50, quality.fogDistance);

    console.log(`🎮 Graphics quality: ${qualityLevel} (pixel ratio: ${quality.pixelRatio})`);

    // Initialize camera
    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    // Initialize input manager
    this.inputManager = new InputManager({ canvas: config.canvas });

    // Initialize third-person camera
    this.thirdPersonCamera = new ThirdPersonCamera({
      camera: this.camera,
      scene: this.scene,
      distance: 10,
      height: 2,
    });

    // Setup FPS counter
    if (this.showFPS) {
      this.fpsElement = document.getElementById('fps-counter');
    }

    // Handle window resize
    window.addEventListener('resize', this.onResize.bind(this));

    // Setup scene
    this.setupScene();

    // Create player
    this.player = new Player({
      scene: this.scene,
      position: new THREE.Vector3(0, 0, 0),
    });

    // Set camera target to player
    this.thirdPersonCamera.setTarget(this.player.mesh);

    // Initialize systems
    this.targetingSystem = new TargetingSystem(this.camera, this.scene);
    this.combatSystem = new CombatSystem(this.player, this.camera);
    this.inventorySystem = new InventorySystem({ slots: 20 });
    this.questSystem = new QuestSystem();
    this.skillSystem = new SkillSystem(this.player, this.targetingSystem, this.camera, 'warrior');

    // Initialize new performance systems
    this.initializePerformanceSystems();

    // Initialize Audio System
    this.audioSystem = new AudioSystem();
    this.audioSystem.setVolume('master', 0.5);

    // Initialize UI
    this.hud = new HUD({
      maxHealth: GAME_CONFIG.PLAYER_MAX_HEALTH,
      maxMana: GAME_CONFIG.PLAYER_MAX_MANA,
    });
    this.dialogUI = new DialogUI();
    this.chatUI = new ChatUI();
    this.floatingText = new FloatingTextSystem(this.camera);
    this.combatLog = new CombatLog();
    this.nameplateSystem = new NameplateSystem(this.camera);
    this.statsPanel = new StatsPanel();
    this.minimap = new Minimap();

    // Initialize optimized nameplates (runs alongside legacy for now)
    const nameplateContainer = document.createElement('div');
    nameplateContainer.id = 'optimized-nameplates';
    nameplateContainer.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;overflow:hidden;';
    document.body.appendChild(nameplateContainer);
    this.optimizedNameplates = new OptimizedNameplateSystem(this.camera, nameplateContainer);

    // Initialize network manager
    this.networkManager = new NetworkManager();
    this.setupNetworkCallbacks();

    // Setup chat UI callback (supports channels now)
    this.chatUI.onSendMessage = (channel: ChatChannel, message: string, targetId?: string) => {
      this.networkManager.sendChat(message);
      this.audioSystem.play('chatSend');
    };

    // Setup quest system callbacks
    this.setupQuestCallbacks();

    // Setup skill system
    this.setupSkillSystem();

    // Spawn entities
    this.spawnEnemies();
    this.spawnNPCs();

    // Setup click handlers
    this.canvas.addEventListener('mousedown', this.onCanvasMouseDown.bind(this));
    this.canvas.addEventListener('click', this.onCanvasClick.bind(this));
    this.canvas.addEventListener('contextmenu', this.onCanvasRightClick.bind(this));

    // Setup keyboard handlers for interaction
    window.addEventListener('keydown', this.onKeyDown.bind(this));

    // Give starting items
    this.inventorySystem.addItem('rusty_sword', 1);
    this.inventorySystem.addGold(10);
    this.hud.setGold(10);

    // PERF: Bind game loop once to avoid per-frame allocation
    this.boundGameLoop = this.gameLoop.bind(this);
  }

  private initializePerformanceSystems(): void {
    // Particle System - GPU-based effects
    this.particleSystem = new ParticleSystem(this.scene);

    // Spatial Partitioning - O(log n) entity queries
    // World bounds: -500 to +500 in x and z
    this.spatialTree = new Quadtree(
      { x: -500, z: -500, width: 1000, height: 1000 },
      8, // Max entities per node
      6  // Max depth
    );

    // Combo System - Lineage II style skill chaining
    this.comboSystem = new ComboSystem('warrior');
    this.setupComboCallbacks();

    // Enhancement System - MU Online style +1 to +15
    this.enhancementSystem = new EnhancementSystem();
    this.socketSystem = new SocketSystem();
    this.setupEnhancementCallbacks();

    // LOD System - Distance-based detail levels
    this.lodSystem = new LODSystem(this.scene, this.camera, {
      highDistance: 20,
      mediumDistance: 50,
      lowDistance: 100,
      spriteDistance: 150,
      cullDistance: 250,
    });

    // Skill Effect Cache - Preloaded effects for instant responsiveness
    this.skillEffectCache = new SkillEffectCache(this.scene);

    // World Chunk Manager - Streaming terrain
    this.worldChunkManager = new WorldChunkManager(
      this.scene,
      { chunkSize: 64, loadDistance: 2, unloadDistance: 4, maxLoadedChunks: 25 },
      async (chunk) => {
        // Called when chunk is loaded - can spawn enemies/NPCs here
        console.log(`Chunk loaded: ${chunk.coord.x}, ${chunk.coord.z}`);
      },
      (chunk) => {
        // Called when chunk is unloaded
        console.log(`Chunk unloaded: ${chunk.coord.x}, ${chunk.coord.z}`);
      }
    );

    // UI Components for performance systems
    this.comboUI = new ComboUI();
    this.enhancementUI = new EnhancementUI();
    this.setupEnhancementUICallbacks();

    console.log('Performance systems initialized');
  }

  private setupComboCallbacks(): void {
    this.comboSystem.onComboStart = (comboName, totalSteps) => {
      this.comboUI.showComboStart(comboName, totalSteps);
      this.audioSystem.play('comboStart');
    };

    this.comboSystem.onComboProgress = (step, totalSteps, bonusDamage, nextSkillName) => {
      this.comboUI.updateComboProgress(step, totalSteps, bonusDamage, nextSkillName);
    };

    this.comboSystem.onComboComplete = (comboName, totalBonusDamage) => {
      this.comboUI.showComboComplete(comboName, totalBonusDamage);
      this.audioSystem.play('comboComplete');
      // Emit celebration particles at player position
      this.particleSystem.emit('level_up', this.player.getPosition(), undefined, 0.5);
    };

    this.comboSystem.onComboBroken = () => {
      this.comboUI.showComboBroken();
    };
  }

  private setupEnhancementCallbacks(): void {
    this.enhancementSystem.onEnhancementAttempt = (result) => {
      if (result.success) {
        this.hud.showNotification(result.message, '#00ff00');
        this.audioSystem.play('enhanceSuccess');
        this.particleSystem.emit('holy_light', this.player.getPosition());
      } else if (result.destroyed) {
        this.hud.showNotification(result.message, '#ff0000');
        this.audioSystem.play('enhanceFail');
        this.particleSystem.emit('death_soul', this.player.getPosition());
      } else {
        this.hud.showNotification(result.message, '#ff8800');
        this.audioSystem.play('enhanceFail');
      }
    };
  }

  private setupEnhancementUICallbacks(): void {
    this.enhancementUI.setCallbacks({
      onEnhance: (itemInstanceId: string, stoneId?: string) => {
        // Get the item from enhanced items or create from inventory
        const existingEnhanced = this.enhancementSystem.getEnhancedItem(itemInstanceId);
        const itemId = existingEnhanced?.itemId ?? 'sword_iron'; // Default to basic sword
        const baseStats = { damage: 10, defense: 0 }; // Base stats for enhancement
        return this.enhancementSystem.enhance(itemInstanceId, itemId, baseStats, stoneId);
      },
      getEnhancedItem: (instanceId: string) => {
        return this.enhancementSystem.getEnhancedItem(instanceId);
      },
      getSuccessRate: (currentLevel: number, stoneId?: string) => {
        return this.enhancementSystem.getSuccessRate(currentLevel, stoneId);
      },
      getEnhancementStones: () => {
        // Get available enhancement stones from inventory
        const allItems = this.inventorySystem.getAllItems();
        const stones = allItems
          .filter((item): item is NonNullable<typeof item> =>
            item !== null && item.itemId.startsWith('enhancement_stone_'))
          .map(item => {
            const tier = item.itemId.replace('enhancement_stone_', '') as 'common' | 'blessed' | 'divine';
            return {
              id: `stone_${item.slot}`,
              tier,
              successBonus: tier === 'common' ? 0 : tier === 'blessed' ? 0.1 : 0.2,
              protectsItem: tier === 'divine',
            };
          });
        return stones;
      },
    });
  }

  // Open enhancement UI - bind to a key like 'U'
  public openEnhancementUI(itemInstanceId?: string): void {
    this.enhancementUI.show(itemInstanceId);
  }

  private setupNetworkCallbacks(): void {
    this.networkManager.events.onConnect = (playerId) => {
      this.isMultiplayer = true;
      this.hud.showNotification('Connected to server!', '#44ff44');
      console.log(`Connected as ${playerId}`);
    };

    this.networkManager.events.onDisconnect = () => {
      this.isMultiplayer = false;
      this.hud.showNotification('Disconnected from server', '#ff4444');
      // Remove all remote players
      this.remotePlayers.forEach((player) => player.destroy());
      this.remotePlayers.clear();
    };

    this.networkManager.events.onPlayerJoin = (playerData) => {
      // Don't add ourselves
      if (playerData.id === this.networkManager.getPlayerId()) return;

      const remotePlayer = new RemotePlayer(this.scene, playerData);
      this.remotePlayers.set(playerData.id, remotePlayer);
      this.hud.showNotification(`${playerData.name} joined`, '#44ff44');
      this.chatUI.addSystemMessage(`${playerData.name} joined the game`);
    };

    this.networkManager.events.onPlayerLeave = (playerId) => {
      const remotePlayer = this.remotePlayers.get(playerId);
      if (remotePlayer) {
        this.chatUI.addSystemMessage(`${remotePlayer.playerName} left the game`);
        remotePlayer.destroy();
        this.remotePlayers.delete(playerId);
      }
    };

    this.networkManager.events.onPlayerMove = (playerId, position, rotation) => {
      const remotePlayer = this.remotePlayers.get(playerId);
      if (remotePlayer) {
        remotePlayer.updatePosition(position, rotation);
      }
    };

    this.networkManager.events.onWorldState = (players) => {
      // Sync all players from world state
      const myId = this.networkManager.getPlayerId();

      players.forEach((playerData) => {
        if (playerData.id === myId) return;

        let remotePlayer = this.remotePlayers.get(playerData.id);
        if (!remotePlayer) {
          // New player we didn't know about
          remotePlayer = new RemotePlayer(this.scene, playerData);
          this.remotePlayers.set(playerData.id, remotePlayer);
        } else {
          // Update existing player
          remotePlayer.updatePosition(playerData.position, playerData.rotation);
        }
      });

      // Remove players that are no longer in the world state
      const playerIds = new Set(players.map((p) => p.id));
      this.remotePlayers.forEach((_, id) => {
        if (!playerIds.has(id)) {
          this.remotePlayers.get(id)?.destroy();
          this.remotePlayers.delete(id);
        }
      });
    };

    this.networkManager.events.onChat = (playerId, playerName, message) => {
      this.chatUI.addMessage(playerName, message);
    };
  }

  private setupQuestCallbacks(): void {
    this.questSystem.onQuestAccepted = (quest) => {
      this.hud.showNotification(`Quest Accepted: ${quest.name}`, '#ffff00');
      this.updateQuestTracker();
      this.updateNPCQuestMarkers();
    };

    this.questSystem.onQuestProgress = () => {
      this.updateQuestTracker();
    };

    this.questSystem.onQuestComplete = (quest) => {
      this.hud.showNotification(`Quest Complete: ${quest.name}`, '#44ff44');
      this.updateNPCQuestMarkers();
    };

    this.questSystem.onQuestTurnedIn = (quest) => {
      // Grant rewards
      this.hud.addExperience(quest.rewards.experience);
      this.inventorySystem.addGold(quest.rewards.gold);
      this.hud.setGold(this.inventorySystem.getGold());

      if (quest.rewards.items) {
        quest.rewards.items.forEach((item) => {
          this.inventorySystem.addItem(item.itemId, item.quantity);
          const itemDef = ITEMS[item.itemId];
          if (itemDef) {
            this.hud.showPickupNotification(itemDef.icon, itemDef.name, item.quantity);
          }
        });
      }

      this.hud.showNotification(`Rewards received!`, '#ffd700');
      this.updateQuestTracker();
      this.updateNPCQuestMarkers();
    };
  }

  private updateQuestTracker(): void {
    this.hud.updateQuests(this.questSystem.getActiveQuests());
  }

  private setupSkillSystem(): void {
    // Initialize action bar with warrior skills
    this.hud.setActionBar(this.skillSystem.getActionBar());

    // Setup HUD skill click callback
    this.hud.onSkillClick = (slotIndex: number) => {
      this.useSkillSlot(slotIndex);
    };

    // Setup skill system events with combo and particle integration
    this.skillSystem.events.onSkillUsed = (skillId, target) => {
      console.log(`Used skill: ${skillId}`);
      this.audioSystem.play('skillCast');

      // Integrate with combo system
      const comboResult = this.comboSystem.onSkillUsed(skillId as SkillId);
      if (comboResult.isCombo) {
        console.log(`Combo ${comboResult.comboStep}/${comboResult.totalSteps}: ${comboResult.bonusDamage}x damage`);
      }

      if (target) {
        this.hud.updateTargetHealth(target.health, target.maxHealth);
        if (!target.isAlive()) {
          this.onEnemyKilled(target);
        }
      }
    };

    this.skillSystem.events.onDamageDealt = (amount: number, position: THREE.Vector3, isCrit: boolean, damageType: string) => {
      const pos = position.clone();
      pos.y += 2;
      this.floatingText.showDamage(amount, pos, damageType, isCrit);
      this.audioSystem.play(isCrit ? 'criticalHit' : 'hit');

      // Emit particles based on damage type
      const particlePos = position.clone();
      particlePos.y += 0.5;
      if (isCrit) {
        this.particleSystem.emit('critical_hit', particlePos);
      } else {
        // Map damage type to particle effect
        const damageParticleMap: Record<string, 'fire_trail' | 'ice_shards' | 'lightning_spark' | 'arcane_burst' | 'holy_light' | 'shadow_tendrils' | 'hit_impact'> = {
          fire: 'fire_trail',
          ice: 'ice_shards',
          lightning: 'lightning_spark',
          arcane: 'arcane_burst',
          holy: 'holy_light',
          shadow: 'shadow_tendrils',
          physical: 'hit_impact',
        };
        const particleType = damageParticleMap[damageType] || 'hit_impact';
        this.particleSystem.emit(particleType, particlePos, undefined, 0.5);
      }
    };

    this.skillSystem.events.onHealingDone = (amount: number, position: THREE.Vector3, isCrit: boolean) => {
      const pos = position.clone();
      pos.y += 2;
      // Emit healing particles
      this.particleSystem.emit('heal_sparkle', position);
      this.floatingText.showHeal(amount, pos, isCrit);
      this.audioSystem.play('heal');
    };

    this.skillSystem.events.onCastStart = (skillId, castTime) => {
      const skill = this.skillSystem.getSkillInfo(skillId);
      if (skill) {
        this.hud.showCastBar(skill.name);
        this.audioSystem.play('castStart');
      }
    };

    this.skillSystem.events.onCastComplete = () => {
      this.hud.hideCastBar();
      this.audioSystem.play('castComplete');
    };

    this.skillSystem.events.onCastInterrupt = () => {
      this.hud.hideCastBar();
      this.hud.showNotification('Interrupted!', '#ff4444');
      this.audioSystem.play('castInterrupt');
    };

    this.skillSystem.events.onBuffApplied = (buff) => {
      this.hud.showNotification(`${buff.name}`, '#00ff00');
      this.audioSystem.play('buffApply');
    };
  }

  private useSkillSlot(slotIndex: number): void {
    const uiOpen = this.dialogUI.isDialogOpen() || this.inventorySystem.isInventoryOpen() || this.chatUI.isChatOpen();
    if (uiOpen) return;

    this.skillSystem.useSkillBySlot(slotIndex, this.enemies);
  }

  private updateNPCQuestMarkers(): void {
    this.npcs.forEach((npc) => {
      const available = this.questSystem.getAvailableQuests(npc.id);
      const turnIn = this.questSystem.getTurnInQuests(npc.id);
      npc.setQuestState(available.length > 0, turnIn.length > 0);
    });
  }


  private setupScene(): void {
    // Darker ambient light (Diablo II inspired)
    const ambientLight = new THREE.AmbientLight(0x404060, 0.3);
    this.scene.add(ambientLight);

    // Eerie moonlight / twilight sun
    const directionalLight = new THREE.DirectionalLight(0xffeedd, 0.6);
    directionalLight.position.set(100, 150, -50);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 4096;
    directionalLight.shadow.mapSize.height = 4096;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 500;
    directionalLight.shadow.camera.left = -200;
    directionalLight.shadow.camera.right = 200;
    directionalLight.shadow.camera.top = 200;
    directionalLight.shadow.camera.bottom = -200;
    this.scene.add(directionalLight);

    // Secondary fill light (bluish, from opposite side)
    const fillLight = new THREE.DirectionalLight(0x6688aa, 0.3);
    fillLight.position.set(-80, 100, 80);
    this.scene.add(fillLight);

    // Create environment (skybox, stars, moon, ground)
    this.environment = new Environment(this.scene);
    this.groundPlane = this.environment.init();

    // Dark atmospheric fog (Diablo II style)
    this.scene.fog = new THREE.FogExp2(0x0a0a15, 0.0025);

    // Add paths and zone overlays on top of the new ground
    this.createPaths();
    this.createZoneOverlays();

    // Add environment zones
    this.addEnvironmentObjects();
    this.addUniqueElements();
  }

  private createTerrain(): void {
    // Main ground with varied coloring
    const groundSize = 1000;
    const groundGeometry = new THREE.PlaneGeometry(groundSize, groundSize, 200, 200);

    // Add height variation to vertices
    const posAttr = groundGeometry.getAttribute('position');
    for (let i = 0; i < posAttr.count; i++) {
      const x = posAttr.getX(i);
      const y = posAttr.getY(i);

      // Create gentle hills and valleys
      let height = 0;
      height += Math.sin(x * 0.02) * Math.cos(y * 0.02) * 3;
      height += Math.sin(x * 0.05 + 1) * Math.cos(y * 0.03) * 2;
      height += Math.sin(x * 0.01) * Math.sin(y * 0.01) * 5;

      // Flatten area near spawn (town area)
      const distFromCenter = Math.sqrt(x * x + y * y);
      if (distFromCenter < 80) {
        height *= distFromCenter / 80;
      }

      posAttr.setZ(i, height);
    }
    groundGeometry.computeVertexNormals();

    // Dark earth tones (Diablo II palette)
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a1a,
      roughness: 0.95,
      metalness: 0.0,
    });

    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    // Store reference for click-to-move raycasting
    this.groundPlane = ground;

    // Add dirt paths
    this.createPaths();

    // Add zone-specific ground overlays
    this.createZoneOverlays();
  }

  private createPaths(): void {
    // Main roads from town (Lineage style cobblestone paths)
    const pathMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a4a3a,
      roughness: 0.8,
    });

    // North road
    const northPath = new THREE.Mesh(
      new THREE.PlaneGeometry(8, 300),
      pathMaterial
    );
    northPath.rotation.x = -Math.PI / 2;
    northPath.position.set(0, 0.05, 150);
    this.scene.add(northPath);

    // East road
    const eastPath = new THREE.Mesh(
      new THREE.PlaneGeometry(300, 8),
      pathMaterial
    );
    eastPath.rotation.x = -Math.PI / 2;
    eastPath.position.set(150, 0.05, 0);
    this.scene.add(eastPath);

    // South road
    const southPath = new THREE.Mesh(
      new THREE.PlaneGeometry(8, 250),
      pathMaterial
    );
    southPath.rotation.x = -Math.PI / 2;
    southPath.position.set(0, 0.05, -125);
    this.scene.add(southPath);

    // West road
    const westPath = new THREE.Mesh(
      new THREE.PlaneGeometry(250, 8),
      pathMaterial
    );
    westPath.rotation.x = -Math.PI / 2;
    westPath.position.set(-125, 0.05, 0);
    this.scene.add(westPath);

    // Town square (cobblestone circle)
    const townSquare = new THREE.Mesh(
      new THREE.CircleGeometry(25, 32),
      new THREE.MeshStandardMaterial({ color: 0x5a5a4a, roughness: 0.7 })
    );
    townSquare.rotation.x = -Math.PI / 2;
    townSquare.position.set(0, 0.06, 0);
    this.scene.add(townSquare);
  }

  private createZoneOverlays(): void {
    // Corrupted/Demonic zone (southwest) - red tinted ground
    const corruptedZone = new THREE.Mesh(
      new THREE.CircleGeometry(80, 32),
      new THREE.MeshStandardMaterial({
        color: 0x3a1a1a,
        roughness: 0.9,
        transparent: true,
        opacity: 0.8,
      })
    );
    corruptedZone.rotation.x = -Math.PI / 2;
    corruptedZone.position.set(-180, 0.03, -180);
    this.scene.add(corruptedZone);

    // Undead zone (south) - pale/gray ground
    const undeadZone = new THREE.Mesh(
      new THREE.CircleGeometry(100, 32),
      new THREE.MeshStandardMaterial({
        color: 0x2a2a2a,
        roughness: 0.95,
        transparent: true,
        opacity: 0.8,
      })
    );
    undeadZone.rotation.x = -Math.PI / 2;
    undeadZone.position.set(-80, 0.03, -250);
    this.scene.add(undeadZone);

    // Elven/Magic zone (east) - slight purple tint
    const magicZone = new THREE.Mesh(
      new THREE.CircleGeometry(70, 32),
      new THREE.MeshStandardMaterial({
        color: 0x2a2a3a,
        roughness: 0.85,
        transparent: true,
        opacity: 0.7,
      })
    );
    magicZone.rotation.x = -Math.PI / 2;
    magicZone.position.set(220, 0.03, 80);
    this.scene.add(magicZone);
  }

  private addEnvironmentObjects(): void {
    // === DARK FOREST ZONE (Northwest) - Twisted, dead trees ===
    this.createDarkForest(-150, 150, 40);

    // === NORMAL FOREST (Northeast) - Healthy trees ===
    this.createNormalForest(120, 180, 30);

    // === CORRUPTED LANDS (Southwest) - Demonic trees ===
    this.createCorruptedForest(-200, -150, 25);

    // === Scattered trees around map ===
    for (let i = 0; i < 150; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 80 + Math.random() * 350;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      // Skip if in special zones
      if (Math.sqrt((x + 150) ** 2 + (z - 150) ** 2) < 60) continue; // Dark forest
      if (Math.sqrt((x - 120) ** 2 + (z - 180) ** 2) < 50) continue; // Normal forest
      if (Math.sqrt((x + 200) ** 2 + (z + 150) ** 2) < 50) continue; // Corrupted

      this.createTree(x, z, 0.7 + Math.random() * 0.6, 'normal');
    }

    // === ROCKS AND BOULDERS ===
    for (let i = 0; i < 120; i++) {
      const x = (Math.random() - 0.5) * 800;
      const z = (Math.random() - 0.5) * 800;
      const scale = 0.5 + Math.random() * 2;

      const rock = new THREE.Mesh(
        new THREE.DodecahedronGeometry(scale, 0),
        new THREE.MeshStandardMaterial({
          color: new THREE.Color().setHSL(0, 0, 0.2 + Math.random() * 0.15),
          roughness: 0.95,
        })
      );
      rock.position.set(x, scale * 0.4, z);
      rock.rotation.set(Math.random(), Math.random(), Math.random());
      rock.castShadow = true;
      this.scene.add(rock);
    }

    // === SKULL PILES (Diablo style, near corrupted areas) ===
    this.createSkullPile(-160, -200);
    this.createSkullPile(-220, -160);
    this.createSkullPile(-100, -280);

    // === BONE PILES (near graveyard) ===
    for (let i = 0; i < 8; i++) {
      this.createBonePile(
        -80 + (Math.random() - 0.5) * 60,
        -250 + (Math.random() - 0.5) * 40
      );
    }

    // === TOWN DECORATIONS ===
    this.createTownDecorations();
  }

  private createTree(x: number, z: number, scale: number, type: 'normal' | 'dead' | 'corrupted'): void {
    const trunkColor = type === 'corrupted' ? 0x2a1a1a : type === 'dead' ? 0x3a3020 : 0x4a3520;
    const foliageColor = type === 'corrupted' ? 0x4a1a2a : type === 'dead' ? 0x3a4a30 : 0x2a4a20;

    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3 * scale, 0.5 * scale, 3 * scale, 6),
      new THREE.MeshStandardMaterial({ color: trunkColor, roughness: 0.9 })
    );
    trunk.position.set(x, 1.5 * scale, z);
    trunk.castShadow = true;
    this.scene.add(trunk);

    if (type !== 'dead') {
      const foliage = new THREE.Mesh(
        new THREE.SphereGeometry(2 * scale, 8, 6),
        new THREE.MeshStandardMaterial({
          color: foliageColor,
          roughness: 0.8,
          ...(type === 'corrupted' && { emissive: 0x1a0010, emissiveIntensity: 0.2 }),
        })
      );
      foliage.position.set(x, 4 * scale, z);
      foliage.scale.y = 0.8;
      foliage.castShadow = true;
      this.scene.add(foliage);
    } else {
      // Dead tree branches
      for (let i = 0; i < 4; i++) {
        const branch = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05 * scale, 0.15 * scale, 1.5 * scale, 4),
          new THREE.MeshStandardMaterial({ color: trunkColor })
        );
        branch.position.set(x, 2.5 * scale, z);
        branch.rotation.z = 0.5 + Math.random() * 0.5;
        branch.rotation.y = (i / 4) * Math.PI * 2;
        this.scene.add(branch);
      }
    }
  }

  private createDarkForest(centerX: number, centerZ: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 50;
      const x = centerX + Math.cos(angle) * radius;
      const z = centerZ + Math.sin(angle) * radius;
      this.createTree(x, z, 0.8 + Math.random() * 0.8, Math.random() > 0.4 ? 'dead' : 'normal');
    }

    // Add eerie fog ground
    const fog = new THREE.Mesh(
      new THREE.CircleGeometry(55, 32),
      new THREE.MeshStandardMaterial({
        color: 0x334433,
        transparent: true,
        opacity: 0.4,
      })
    );
    fog.rotation.x = -Math.PI / 2;
    fog.position.set(centerX, 0.08, centerZ);
    this.scene.add(fog);

    // Eerie light
    const eerieLight = new THREE.PointLight(0x44aa44, 0.5, 60);
    eerieLight.position.set(centerX, 5, centerZ);
    this.scene.add(eerieLight);
  }

  private createNormalForest(centerX: number, centerZ: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 45;
      const x = centerX + Math.cos(angle) * radius;
      const z = centerZ + Math.sin(angle) * radius;
      this.createTree(x, z, 0.9 + Math.random() * 0.7, 'normal');
    }
  }

  private createCorruptedForest(centerX: number, centerZ: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 45;
      const x = centerX + Math.cos(angle) * radius;
      const z = centerZ + Math.sin(angle) * radius;
      this.createTree(x, z, 0.6 + Math.random() * 0.6, 'corrupted');
    }

    // Demonic glow
    const demonLight = new THREE.PointLight(0xff2200, 0.8, 50);
    demonLight.position.set(centerX, 3, centerZ);
    this.scene.add(demonLight);
  }

  private createSkullPile(x: number, z: number): void {
    const pileBase = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 8, 6),
      new THREE.MeshStandardMaterial({ color: 0xd4c4a4, roughness: 0.9 })
    );
    pileBase.position.set(x, 0.5, z);
    pileBase.scale.y = 0.5;
    this.scene.add(pileBase);

    // Individual skulls
    for (let i = 0; i < 5; i++) {
      const skull = new THREE.Mesh(
        new THREE.SphereGeometry(0.3 + Math.random() * 0.2, 6, 5),
        new THREE.MeshStandardMaterial({ color: 0xc4b494 })
      );
      skull.position.set(
        x + (Math.random() - 0.5) * 2,
        0.3 + Math.random() * 0.5,
        z + (Math.random() - 0.5) * 2
      );
      this.scene.add(skull);
    }
  }

  private createBonePile(x: number, z: number): void {
    for (let i = 0; i < 6; i++) {
      const bone = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.08, 0.8, 6),
        new THREE.MeshStandardMaterial({ color: 0xd4c4a4 })
      );
      bone.position.set(
        x + (Math.random() - 0.5) * 1.5,
        0.1,
        z + (Math.random() - 0.5) * 1.5
      );
      bone.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.PI / 2);
      this.scene.add(bone);
    }
  }

  private createTownDecorations(): void {
    // Town well (center)
    const wellBase = new THREE.Mesh(
      new THREE.CylinderGeometry(2, 2.2, 1, 12),
      new THREE.MeshStandardMaterial({ color: 0x5a5a5a })
    );
    wellBase.position.set(0, 0.5, 0);
    this.scene.add(wellBase);

    const wellWater = new THREE.Mesh(
      new THREE.CircleGeometry(1.8, 16),
      new THREE.MeshStandardMaterial({ color: 0x224466, metalness: 0.3 })
    );
    wellWater.rotation.x = -Math.PI / 2;
    wellWater.position.set(0, 0.95, 0);
    this.scene.add(wellWater);

    // Well roof structure
    const wellPost1 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.15, 3, 6),
      new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
    );
    wellPost1.position.set(-1.5, 2, 0);
    this.scene.add(wellPost1);

    const wellPost2 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.15, 3, 6),
      new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
    );
    wellPost2.position.set(1.5, 2, 0);
    this.scene.add(wellPost2);

    const wellRoof = new THREE.Mesh(
      new THREE.ConeGeometry(2.5, 1.5, 4),
      new THREE.MeshStandardMaterial({ color: 0x6a4a3a })
    );
    wellRoof.position.set(0, 4, 0);
    wellRoof.rotation.y = Math.PI / 4;
    this.scene.add(wellRoof);

    // Lantern posts around town
    const lanternPositions = [
      { x: 15, z: 15 }, { x: -15, z: 15 }, { x: 15, z: -15 }, { x: -15, z: -15 },
      { x: 25, z: 0 }, { x: -25, z: 0 }, { x: 0, z: 25 }, { x: 0, z: -25 },
    ];

    lanternPositions.forEach((pos) => {
      this.createLanternPost(pos.x, pos.z);
    });

    // Wooden crates near merchants
    this.createCrate(6, -8);
    this.createCrate(7.5, -7);
    this.createCrate(9, -6);

    // Barrels
    this.createBarrel(-10, 3);
    this.createBarrel(-11.5, 2);

    // Notice board
    this.createNoticeBoard(-5, -10);
  }

  private createLanternPost(x: number, z: number): void {
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.15, 4, 6),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a })
    );
    post.position.set(x, 2, z);
    this.scene.add(post);

    const lantern = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.6, 0.4),
      new THREE.MeshStandardMaterial({
        color: 0xffaa44,
        emissive: 0xff8800,
        emissiveIntensity: 0.8,
      })
    );
    lantern.position.set(x, 4.3, z);
    this.scene.add(lantern);

    const light = new THREE.PointLight(0xffaa44, 0.8, 15);
    light.position.set(x, 4.3, z);
    this.scene.add(light);
  }

  private createCrate(x: number, z: number): void {
    const crate = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshStandardMaterial({ color: 0x6a5030 })
    );
    crate.position.set(x, 0.5, z);
    crate.rotation.y = Math.random() * 0.3;
    crate.castShadow = true;
    this.scene.add(crate);
  }

  private createBarrel(x: number, z: number): void {
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 1.2, 12),
      new THREE.MeshStandardMaterial({ color: 0x5a4020 })
    );
    barrel.position.set(x, 0.6, z);
    barrel.castShadow = true;
    this.scene.add(barrel);
  }

  private createNoticeBoard(x: number, z: number): void {
    // Posts
    const post1 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.1, 2.5, 6),
      new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
    );
    post1.position.set(x - 0.8, 1.25, z);
    this.scene.add(post1);

    const post2 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.1, 2.5, 6),
      new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
    );
    post2.position.set(x + 0.8, 1.25, z);
    this.scene.add(post2);

    // Board
    const board = new THREE.Mesh(
      new THREE.BoxGeometry(2, 1.5, 0.1),
      new THREE.MeshStandardMaterial({ color: 0x5a4a3a })
    );
    board.position.set(x, 2, z);
    this.scene.add(board);

    // Papers
    const paper = new THREE.Mesh(
      new THREE.PlaneGeometry(0.6, 0.8),
      new THREE.MeshStandardMaterial({ color: 0xddcc99 })
    );
    paper.position.set(x - 0.4, 2.1, z + 0.06);
    this.scene.add(paper);

    const paper2 = new THREE.Mesh(
      new THREE.PlaneGeometry(0.5, 0.7),
      new THREE.MeshStandardMaterial({ color: 0xccbb88 })
    );
    paper2.position.set(x + 0.3, 1.9, z + 0.06);
    paper2.rotation.z = 0.1;
    this.scene.add(paper2);
  }

  private addUniqueElements(): void {
    // === DEMONIC PORTAL (Diablo II style Dark Portal) ===
    this.createDemonGate(-180, -180);

    // === RUINED CATHEDRAL (Diablo style) ===
    this.createRuinedCathedral(180, -120);

    // === ORC ENCAMPMENT (WoW style) ===
    this.createOrcCamp(-250, 50);

    // === ELVEN RUINS (Lineage style) ===
    this.createElvenRuins(220, 80);

    // === DARK CEMETERY with Crypts (Diablo II) ===
    this.createDarkCemetery(-80, -280);

    // === WAYPOINT SHRINES (Diablo II teleport points) ===
    this.createWaypoint(0, 60);      // North of town
    this.createWaypoint(150, 0);     // East road
    this.createWaypoint(-150, 0);    // West road
    this.createWaypoint(0, -150);    // South road

    // === ANCIENT MONUMENT (WoW style) ===
    this.createAncientMonument(100, 200);

    // === GOBLIN CAVE (WoW/Lineage) ===
    this.createGoblinCave(-280, -80);

    // === MAGIC TOWER RUINS ===
    this.createMageTowerRuins(280, 150);

    // === BLOOD ALTAR (Diablo) ===
    this.createBloodAltar(-200, -220);

    // === DEFENSIVE WALLS around town ===
    this.createTownWalls();
  }

  // === DIABLO II STYLE DEMON GATE ===
  private createDemonGate(x: number, z: number): void {
    // Portal frame - dark stone arch
    const frameColor = 0x2a1a1a;

    // Left pillar
    const leftPillar = new THREE.Mesh(
      new THREE.BoxGeometry(3, 15, 3),
      new THREE.MeshStandardMaterial({ color: frameColor, roughness: 0.9 })
    );
    leftPillar.position.set(x - 6, 7.5, z);
    leftPillar.castShadow = true;
    this.scene.add(leftPillar);

    // Right pillar
    const rightPillar = new THREE.Mesh(
      new THREE.BoxGeometry(3, 15, 3),
      new THREE.MeshStandardMaterial({ color: frameColor, roughness: 0.9 })
    );
    rightPillar.position.set(x + 6, 7.5, z);
    rightPillar.castShadow = true;
    this.scene.add(rightPillar);

    // Top arch
    const arch = new THREE.Mesh(
      new THREE.BoxGeometry(15, 3, 3),
      new THREE.MeshStandardMaterial({ color: frameColor, roughness: 0.9 })
    );
    arch.position.set(x, 14, z);
    this.scene.add(arch);

    // Demonic skulls on pillars
    const skullMat = new THREE.MeshStandardMaterial({ color: 0x4a3a3a });
    [-6, 6].forEach((dx) => {
      const skull = new THREE.Mesh(new THREE.SphereGeometry(1.2, 8, 6), skullMat);
      skull.position.set(x + dx, 12, z + 1.8);
      skull.scale.z = 0.7;
      this.scene.add(skull);
    });

    // Portal vortex (spinning red/black energy)
    const portalGeom = new THREE.CircleGeometry(5, 32);
    const portalMat = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xaa0000,
      emissiveIntensity: 1,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    const portal = new THREE.Mesh(portalGeom, portalMat);
    portal.position.set(x, 7, z);
    this.scene.add(portal);

    // Inner dark core
    const core = new THREE.Mesh(
      new THREE.CircleGeometry(2.5, 32),
      new THREE.MeshStandardMaterial({
        color: 0x000000,
        emissive: 0x330000,
        emissiveIntensity: 0.5,
        side: THREE.DoubleSide,
      })
    );
    core.position.set(x, 7, z + 0.1);
    this.scene.add(core);

    // Demonic red glow
    const portalLight = new THREE.PointLight(0xff2200, 3, 40);
    portalLight.position.set(x, 7, z + 3);
    this.scene.add(portalLight);

    // Ground corruption
    const corruption = new THREE.Mesh(
      new THREE.CircleGeometry(15, 32),
      new THREE.MeshStandardMaterial({
        color: 0x1a0505,
        emissive: 0x220000,
        emissiveIntensity: 0.3,
      })
    );
    corruption.rotation.x = -Math.PI / 2;
    corruption.position.set(x, 0.05, z);
    this.scene.add(corruption);

    // Flame braziers
    [-8, 8].forEach((dx) => {
      this.createFlameBrazier(x + dx, z + 5);
    });
  }

  private createFlameBrazier(x: number, z: number): void {
    // Brazier stand
    const stand = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3, 0.5, 3, 8),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
    );
    stand.position.set(x, 1.5, z);
    this.scene.add(stand);

    // Bowl
    const bowl = new THREE.Mesh(
      new THREE.CylinderGeometry(1, 0.6, 0.8, 8),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a })
    );
    bowl.position.set(x, 3.2, z);
    this.scene.add(bowl);

    // Fire
    const fire = new THREE.Mesh(
      new THREE.ConeGeometry(0.7, 2, 8),
      new THREE.MeshStandardMaterial({
        color: 0xff4400,
        emissive: 0xff2200,
        emissiveIntensity: 1,
        transparent: true,
        opacity: 0.9,
      })
    );
    fire.position.set(x, 4.5, z);
    this.scene.add(fire);

    // Fire light
    const light = new THREE.PointLight(0xff4400, 1.5, 12);
    light.position.set(x, 4.5, z);
    this.scene.add(light);
  }

  // === DIABLO STYLE RUINED CATHEDRAL ===
  private createRuinedCathedral(x: number, z: number): void {
    const stoneColor = 0x3a3a3a;

    // Main floor
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(40, 0.5, 25),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.95 })
    );
    floor.position.set(x, 0.25, z);
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Broken walls
    const wallPositions = [
      { dx: -18, dz: 0, w: 2, h: 12, d: 25, broken: false },
      { dx: 18, dz: 0, w: 2, h: 8, d: 25, broken: true },
      { dx: 0, dz: -11, w: 36, h: 10, d: 2, broken: true },
    ];

    wallPositions.forEach((w) => {
      const wall = new THREE.Mesh(
        new THREE.BoxGeometry(w.w, w.h, w.d),
        new THREE.MeshStandardMaterial({ color: stoneColor, roughness: 0.9 })
      );
      wall.position.set(x + w.dx, w.h / 2, z + w.dz);
      wall.castShadow = true;
      this.scene.add(wall);
    });

    // Gothic arched windows (broken)
    [-6, 0, 6].forEach((dz) => {
      const windowFrame = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 6, 3),
        new THREE.MeshStandardMaterial({ color: stoneColor })
      );
      windowFrame.position.set(x - 18, 6, z + dz);
      this.scene.add(windowFrame);
    });

    // Altar at the back
    const altar = new THREE.Mesh(
      new THREE.BoxGeometry(6, 2, 3),
      new THREE.MeshStandardMaterial({ color: 0x4a4a4a })
    );
    altar.position.set(x, 1, z - 8);
    this.scene.add(altar);

    // Broken columns
    const columnPositions = [
      { dx: -12, dz: -5 }, { dx: -12, dz: 5 },
      { dx: 12, dz: -5 }, { dx: 12, dz: 5 },
      { dx: -6, dz: -5 }, { dx: -6, dz: 5 },
      { dx: 6, dz: -5 }, { dx: 6, dz: 5 },
    ];

    columnPositions.forEach((c, i) => {
      const height = i % 3 === 0 ? 4 : 8;
      const column = new THREE.Mesh(
        new THREE.CylinderGeometry(0.8, 1, height, 8),
        new THREE.MeshStandardMaterial({ color: stoneColor })
      );
      column.position.set(x + c.dx, height / 2, z + c.dz);
      column.castShadow = true;
      this.scene.add(column);
    });

    // Candles on altar
    for (let i = 0; i < 5; i++) {
      const candle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, 0.1, 0.5, 8),
        new THREE.MeshStandardMaterial({ color: 0xddccaa })
      );
      candle.position.set(x - 2 + i, 2.3, z - 8);
      this.scene.add(candle);

      const flame = new THREE.Mesh(
        new THREE.ConeGeometry(0.08, 0.2, 6),
        new THREE.MeshStandardMaterial({
          color: 0xffaa00,
          emissive: 0xff8800,
          emissiveIntensity: 1,
        })
      );
      flame.position.set(x - 2 + i, 2.65, z - 8);
      this.scene.add(flame);
    }

    // Eerie light inside
    const eerieLight = new THREE.PointLight(0x6666aa, 0.5, 30);
    eerieLight.position.set(x, 6, z);
    this.scene.add(eerieLight);

    // Scattered debris
    for (let i = 0; i < 15; i++) {
      const debris = new THREE.Mesh(
        new THREE.BoxGeometry(
          0.5 + Math.random() * 1.5,
          0.3 + Math.random() * 0.5,
          0.5 + Math.random() * 1.5
        ),
        new THREE.MeshStandardMaterial({ color: stoneColor })
      );
      debris.position.set(
        x + (Math.random() - 0.5) * 30,
        0.3,
        z + (Math.random() - 0.5) * 20
      );
      debris.rotation.y = Math.random() * Math.PI;
      this.scene.add(debris);
    }
  }

  // === WOW STYLE ORC ENCAMPMENT ===
  private createOrcCamp(x: number, z: number): void {
    // Wooden palisade walls
    for (let i = 0; i < 24; i++) {
      const angle = (i / 24) * Math.PI * 2;
      const radius = 20;
      const spike = new THREE.Mesh(
        new THREE.CylinderGeometry(0.3, 0.5, 5, 6),
        new THREE.MeshStandardMaterial({ color: 0x4a3a20 })
      );
      spike.position.set(
        x + Math.cos(angle) * radius,
        2.5,
        z + Math.sin(angle) * radius
      );
      spike.rotation.z = (Math.random() - 0.5) * 0.1;
      this.scene.add(spike);

      // Pointed top
      const point = new THREE.Mesh(
        new THREE.ConeGeometry(0.4, 1, 6),
        new THREE.MeshStandardMaterial({ color: 0x3a2a15 })
      );
      point.position.set(
        x + Math.cos(angle) * radius,
        5.5,
        z + Math.sin(angle) * radius
      );
      this.scene.add(point);
    }

    // Orc tents (leather huts)
    const tentPositions = [
      { dx: -8, dz: 0 }, { dx: 5, dz: -6 }, { dx: 6, dz: 8 },
    ];

    tentPositions.forEach((t) => {
      // Tent frame
      const tent = new THREE.Mesh(
        new THREE.ConeGeometry(4, 5, 6),
        new THREE.MeshStandardMaterial({ color: 0x5a4a30 })
      );
      tent.position.set(x + t.dx, 2.5, z + t.dz);
      this.scene.add(tent);

      // Tent entrance (darker)
      const entrance = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 3),
        new THREE.MeshStandardMaterial({ color: 0x2a2015, side: THREE.DoubleSide })
      );
      entrance.position.set(x + t.dx + 2, 1.5, z + t.dz);
      entrance.rotation.y = Math.PI / 2;
      this.scene.add(entrance);
    });

    // Central bonfire
    const firepit = new THREE.Mesh(
      new THREE.CylinderGeometry(2, 2.5, 0.5, 12),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a })
    );
    firepit.position.set(x, 0.25, z);
    this.scene.add(firepit);

    const fire = new THREE.Mesh(
      new THREE.ConeGeometry(1.5, 4, 8),
      new THREE.MeshStandardMaterial({
        color: 0xff4400,
        emissive: 0xff2200,
        emissiveIntensity: 1,
      })
    );
    fire.position.set(x, 2.5, z);
    this.scene.add(fire);

    const fireLight = new THREE.PointLight(0xff6600, 2, 25);
    fireLight.position.set(x, 3, z);
    this.scene.add(fireLight);

    // Weapon rack
    const rack = new THREE.Mesh(
      new THREE.BoxGeometry(3, 2, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x4a3a20 })
    );
    rack.position.set(x - 3, 1, z + 10);
    this.scene.add(rack);

    // Horde banner
    const bannerPole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.15, 8, 6),
      new THREE.MeshStandardMaterial({ color: 0x3a2a1a })
    );
    bannerPole.position.set(x, 4, z - 15);
    this.scene.add(bannerPole);

    const banner = new THREE.Mesh(
      new THREE.PlaneGeometry(3, 4),
      new THREE.MeshStandardMaterial({
        color: 0x8b0000,
        side: THREE.DoubleSide,
      })
    );
    banner.position.set(x + 1.5, 6, z - 15);
    this.scene.add(banner);

    // Skull totems
    [-15, 15].forEach((dx) => {
      const totemPole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.4, 0.5, 6, 6),
        new THREE.MeshStandardMaterial({ color: 0x4a3a20 })
      );
      totemPole.position.set(x + dx, 3, z);
      this.scene.add(totemPole);

      const skull = new THREE.Mesh(
        new THREE.SphereGeometry(0.8, 8, 6),
        new THREE.MeshStandardMaterial({ color: 0xccbb99 })
      );
      skull.position.set(x + dx, 6.5, z);
      skull.scale.z = 0.7;
      this.scene.add(skull);
    });
  }

  // === LINEAGE STYLE ELVEN RUINS ===
  private createElvenRuins(x: number, z: number): void {
    const marbleColor = 0x9999aa;
    const goldColor = 0xccaa44;

    // Circular platform
    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(18, 20, 1, 32),
      new THREE.MeshStandardMaterial({ color: marbleColor, roughness: 0.3 })
    );
    platform.position.set(x, 0.5, z);
    platform.receiveShadow = true;
    this.scene.add(platform);

    // Inner ring pattern
    const innerRing = new THREE.Mesh(
      new THREE.RingGeometry(8, 10, 32),
      new THREE.MeshStandardMaterial({
        color: goldColor,
        emissive: 0x886622,
        emissiveIntensity: 0.3,
        side: THREE.DoubleSide,
      })
    );
    innerRing.rotation.x = -Math.PI / 2;
    innerRing.position.set(x, 1.02, z);
    this.scene.add(innerRing);

    // Elegant pillars
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const radius = 15;
      const height = i % 2 === 0 ? 10 : 6; // Alternating heights (some broken)

      // Pillar base
      const base = new THREE.Mesh(
        new THREE.CylinderGeometry(1.2, 1.5, 1, 8),
        new THREE.MeshStandardMaterial({ color: marbleColor })
      );
      base.position.set(
        x + Math.cos(angle) * radius,
        0.5,
        z + Math.sin(angle) * radius
      );
      this.scene.add(base);

      // Pillar shaft
      const pillar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.8, 0.9, height, 12),
        new THREE.MeshStandardMaterial({ color: marbleColor, roughness: 0.3 })
      );
      pillar.position.set(
        x + Math.cos(angle) * radius,
        height / 2 + 1,
        z + Math.sin(angle) * radius
      );
      pillar.castShadow = true;
      this.scene.add(pillar);

      // Ornate capital (if not broken)
      if (height === 10) {
        const capital = new THREE.Mesh(
          new THREE.CylinderGeometry(1.2, 0.8, 1, 8),
          new THREE.MeshStandardMaterial({ color: marbleColor })
        );
        capital.position.set(
          x + Math.cos(angle) * radius,
          height + 1.5,
          z + Math.sin(angle) * radius
        );
        this.scene.add(capital);
      }
    }

    // Central magical crystal
    const crystal = new THREE.Mesh(
      new THREE.OctahedronGeometry(2),
      new THREE.MeshStandardMaterial({
        color: 0x88aaff,
        emissive: 0x4466aa,
        emissiveIntensity: 0.8,
        transparent: true,
        opacity: 0.9,
      })
    );
    crystal.position.set(x, 5, z);
    crystal.rotation.y = Math.PI / 4;
    this.scene.add(crystal);

    // Crystal glow
    const crystalLight = new THREE.PointLight(0x88aaff, 2, 30);
    crystalLight.position.set(x, 5, z);
    this.scene.add(crystalLight);

    // Floating runes around crystal
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const rune = new THREE.Mesh(
        new THREE.PlaneGeometry(0.8, 0.8),
        new THREE.MeshStandardMaterial({
          color: 0xaaccff,
          emissive: 0x6688cc,
          emissiveIntensity: 1,
          transparent: true,
          opacity: 0.7,
          side: THREE.DoubleSide,
        })
      );
      rune.position.set(
        x + Math.cos(angle) * 3,
        4 + Math.sin(i) * 0.5,
        z + Math.sin(angle) * 3
      );
      rune.rotation.y = angle;
      this.scene.add(rune);
    }

    // Overgrown vines on broken pillars
    for (let i = 0; i < 4; i++) {
      const angle = ((i * 2 + 1) / 8) * Math.PI * 2;
      const vine = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, 0.15, 5, 6),
        new THREE.MeshStandardMaterial({ color: 0x2a4a2a })
      );
      vine.position.set(
        x + Math.cos(angle) * 15,
        4,
        z + Math.sin(angle) * 15
      );
      vine.rotation.z = 0.3;
      this.scene.add(vine);
    }
  }

  // === DIABLO II DARK CEMETERY ===
  private createDarkCemetery(x: number, z: number): void {
    // Cemetery ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(80, 60),
      new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.95 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(x, 0.02, z);
    this.scene.add(ground);

    // Iron fence
    for (let i = -35; i <= 35; i += 3) {
      // Front and back fence
      [-28, 28].forEach((dz) => {
        const fencePost = new THREE.Mesh(
          new THREE.CylinderGeometry(0.1, 0.1, 3, 6),
          new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
        );
        fencePost.position.set(x + i, 1.5, z + dz);
        this.scene.add(fencePost);

        // Pointed top
        const point = new THREE.Mesh(
          new THREE.ConeGeometry(0.15, 0.4, 6),
          new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
        );
        point.position.set(x + i, 3.2, z + dz);
        this.scene.add(point);
      });
    }

    // Gravestones in rows
    const rows = [
      { dz: -20, count: 8 },
      { dz: -10, count: 10 },
      { dz: 0, count: 8 },
      { dz: 10, count: 10 },
      { dz: 20, count: 8 },
    ];

    rows.forEach((row) => {
      for (let i = 0; i < row.count; i++) {
        const dx = -30 + (i + 1) * (60 / (row.count + 1));
        const height = 1 + Math.random() * 1.5;

        // Gravestone
        const grave = new THREE.Mesh(
          new THREE.BoxGeometry(1.2, height, 0.4),
          new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.95 })
        );
        grave.position.set(x + dx, height / 2, z + row.dz);
        grave.rotation.y = (Math.random() - 0.5) * 0.2;
        grave.rotation.z = (Math.random() - 0.5) * 0.1;
        this.scene.add(grave);

        // Rounded top
        const top = new THREE.Mesh(
          new THREE.SphereGeometry(0.6, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2),
          new THREE.MeshStandardMaterial({ color: 0x4a4a4a })
        );
        top.position.set(x + dx, height, z + row.dz);
        this.scene.add(top);
      }
    });

    // Central mausoleum/crypt
    const cryptBase = new THREE.Mesh(
      new THREE.BoxGeometry(10, 4, 8),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a })
    );
    cryptBase.position.set(x, 2, z);
    this.scene.add(cryptBase);

    const cryptRoof = new THREE.Mesh(
      new THREE.ConeGeometry(7, 3, 4),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
    );
    cryptRoof.position.set(x, 5.5, z);
    cryptRoof.rotation.y = Math.PI / 4;
    this.scene.add(cryptRoof);

    // Crypt entrance
    const entrance = new THREE.Mesh(
      new THREE.BoxGeometry(3, 3.5, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x1a1a1a })
    );
    entrance.position.set(x, 1.75, z + 4.2);
    this.scene.add(entrance);

    // Crypt door (iron gates)
    const gate = new THREE.Mesh(
      new THREE.PlaneGeometry(2.5, 3),
      new THREE.MeshStandardMaterial({
        color: 0x3a3a3a,
        side: THREE.DoubleSide,
      })
    );
    gate.position.set(x, 1.75, z + 4.3);
    this.scene.add(gate);

    // Dead trees
    const deadTreePositions = [
      { dx: -25, dz: -15 }, { dx: 30, dz: 10 },
      { dx: -20, dz: 20 }, { dx: 25, dz: -20 },
    ];

    deadTreePositions.forEach((t) => {
      this.createTree(x + t.dx, z + t.dz, 1.2, 'dead');
    });

    // Fog
    const fog = new THREE.Mesh(
      new THREE.PlaneGeometry(80, 60),
      new THREE.MeshStandardMaterial({
        color: 0x445566,
        transparent: true,
        opacity: 0.25,
      })
    );
    fog.rotation.x = -Math.PI / 2;
    fog.position.set(x, 0.5, z);
    this.scene.add(fog);

    // Eerie glow from crypt
    const cryptLight = new THREE.PointLight(0x44ff44, 0.5, 15);
    cryptLight.position.set(x, 2, z + 5);
    this.scene.add(cryptLight);

    // Ambient cemetery light
    const ambientLight = new THREE.PointLight(0x6666aa, 0.3, 50);
    ambientLight.position.set(x, 10, z);
    this.scene.add(ambientLight);
  }

  // === DIABLO II WAYPOINTS ===
  private createWaypoint(x: number, z: number): void {
    // Stone platform
    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(3, 3.5, 0.5, 8),
      new THREE.MeshStandardMaterial({ color: 0x4a4a5a, roughness: 0.7 })
    );
    platform.position.set(x, 0.25, z);
    this.scene.add(platform);

    // Inner magical circle
    const circle = new THREE.Mesh(
      new THREE.RingGeometry(1.5, 2.5, 32),
      new THREE.MeshStandardMaterial({
        color: 0x4488ff,
        emissive: 0x2244aa,
        emissiveIntensity: 0.8,
        side: THREE.DoubleSide,
      })
    );
    circle.rotation.x = -Math.PI / 2;
    circle.position.set(x, 0.52, z);
    this.scene.add(circle);

    // Glowing runes
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const rune = new THREE.Mesh(
        new THREE.PlaneGeometry(0.5, 0.5),
        new THREE.MeshStandardMaterial({
          color: 0x88aaff,
          emissive: 0x4466cc,
          emissiveIntensity: 1,
          side: THREE.DoubleSide,
        })
      );
      rune.rotation.x = -Math.PI / 2;
      rune.position.set(x + Math.cos(angle) * 2, 0.53, z + Math.sin(angle) * 2);
      this.scene.add(rune);
    }

    // Central pillar of light
    const lightBeam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3, 0.3, 8, 8),
      new THREE.MeshStandardMaterial({
        color: 0x88aaff,
        emissive: 0x4488ff,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.4,
      })
    );
    lightBeam.position.set(x, 4.5, z);
    this.scene.add(lightBeam);

    // Waypoint glow
    const waypointLight = new THREE.PointLight(0x4488ff, 1.5, 12);
    waypointLight.position.set(x, 2, z);
    this.scene.add(waypointLight);
  }

  // === ANCIENT MONUMENT ===
  private createAncientMonument(x: number, z: number): void {
    // Base platform
    const base = new THREE.Mesh(
      new THREE.BoxGeometry(12, 2, 12),
      new THREE.MeshStandardMaterial({ color: 0x5a5a5a })
    );
    base.position.set(x, 1, z);
    this.scene.add(base);

    // Giant stone warrior statue
    // Body
    const body = new THREE.Mesh(
      new THREE.CylinderGeometry(2, 2.5, 8, 8),
      new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.8 })
    );
    body.position.set(x, 6, z);
    this.scene.add(body);

    // Head
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 8, 6),
      new THREE.MeshStandardMaterial({ color: 0x6a6a6a })
    );
    head.position.set(x, 11, z);
    head.scale.y = 1.2;
    this.scene.add(head);

    // Shoulders/arms
    [-2.5, 2.5].forEach((dx) => {
      const arm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.8, 0.8, 5, 6),
        new THREE.MeshStandardMaterial({ color: 0x6a6a6a })
      );
      arm.position.set(x + dx, 7, z);
      arm.rotation.z = dx > 0 ? 0.3 : -0.3;
      this.scene.add(arm);
    });

    // Sword (held upright)
    const swordBlade = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 8, 0.1),
      new THREE.MeshStandardMaterial({ color: 0x8a8a9a, metalness: 0.5 })
    );
    swordBlade.position.set(x + 3.5, 10, z);
    this.scene.add(swordBlade);

    const swordHilt = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.5, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x5a4a3a })
    );
    swordHilt.position.set(x + 3.5, 6, z);
    this.scene.add(swordHilt);

    // Glowing eyes
    [-0.5, 0.5].forEach((dx) => {
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.2),
        new THREE.MeshStandardMaterial({
          color: 0x88ff88,
          emissive: 0x44aa44,
          emissiveIntensity: 1,
        })
      );
      eye.position.set(x + dx, 11.3, z + 1.3);
      this.scene.add(eye);
    });

    // Eye glow
    const eyeLight = new THREE.PointLight(0x88ff88, 0.5, 10);
    eyeLight.position.set(x, 11, z + 2);
    this.scene.add(eyeLight);
  }

  // === GOBLIN CAVE ===
  private createGoblinCave(x: number, z: number): void {
    // Cave entrance (rocky outcrop)
    const caveRock = new THREE.Mesh(
      new THREE.SphereGeometry(12, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.95, side: THREE.DoubleSide })
    );
    caveRock.position.set(x, 0, z);
    caveRock.scale.set(1.5, 1, 1);
    this.scene.add(caveRock);

    // Dark cave entrance
    const entrance = new THREE.Mesh(
      new THREE.CircleGeometry(5, 16),
      new THREE.MeshStandardMaterial({ color: 0x0a0a0a })
    );
    entrance.position.set(x + 8, 5, z);
    entrance.rotation.y = -Math.PI / 2;
    this.scene.add(entrance);

    // Scattered loot/bones near entrance
    for (let i = 0; i < 8; i++) {
      const bone = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.08, 0.6, 6),
        new THREE.MeshStandardMaterial({ color: 0xccbb99 })
      );
      bone.position.set(
        x + 10 + Math.random() * 8,
        0.1,
        z + (Math.random() - 0.5) * 10
      );
      bone.rotation.set(Math.PI / 2, Math.random() * Math.PI, 0);
      this.scene.add(bone);
    }

    // Crude warning signs
    const sign = new THREE.Mesh(
      new THREE.BoxGeometry(2, 1.5, 0.2),
      new THREE.MeshStandardMaterial({ color: 0x5a4a30 })
    );
    sign.position.set(x + 15, 1.5, z - 5);
    sign.rotation.y = 0.3;
    this.scene.add(sign);

    const signPole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.1, 3, 6),
      new THREE.MeshStandardMaterial({ color: 0x4a3a20 })
    );
    signPole.position.set(x + 15, 1.5, z - 5);
    this.scene.add(signPole);

    // Torch near entrance
    this.createFlameBrazier(x + 12, z + 6);
    this.createFlameBrazier(x + 12, z - 6);
  }

  // === MAGE TOWER RUINS ===
  private createMageTowerRuins(x: number, z: number): void {
    // Tower base
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(8, 10, 3, 12),
      new THREE.MeshStandardMaterial({ color: 0x4a4a5a })
    );
    base.position.set(x, 1.5, z);
    this.scene.add(base);

    // Broken tower section
    const tower = new THREE.Mesh(
      new THREE.CylinderGeometry(6, 7, 15, 12, 1, false, 0, Math.PI * 1.7),
      new THREE.MeshStandardMaterial({ color: 0x5a5a6a, side: THREE.DoubleSide })
    );
    tower.position.set(x, 10.5, z);
    tower.castShadow = true;
    this.scene.add(tower);

    // Rubble from collapsed section
    for (let i = 0; i < 12; i++) {
      const rubble = new THREE.Mesh(
        new THREE.DodecahedronGeometry(0.5 + Math.random() * 1.5),
        new THREE.MeshStandardMaterial({ color: 0x5a5a6a })
      );
      rubble.position.set(
        x + 8 + Math.random() * 6,
        0.5,
        z + (Math.random() - 0.5) * 8
      );
      this.scene.add(rubble);
    }

    // Arcane energy swirling at top
    const arcaneEnergy = new THREE.Mesh(
      new THREE.TorusGeometry(4, 0.5, 8, 32),
      new THREE.MeshStandardMaterial({
        color: 0xaa44ff,
        emissive: 0x6622aa,
        emissiveIntensity: 0.8,
        transparent: true,
        opacity: 0.6,
      })
    );
    arcaneEnergy.position.set(x, 18, z);
    arcaneEnergy.rotation.x = Math.PI / 2;
    this.scene.add(arcaneEnergy);

    // Arcane light
    const arcaneLight = new THREE.PointLight(0xaa44ff, 2, 25);
    arcaneLight.position.set(x, 18, z);
    this.scene.add(arcaneLight);

    // Floating book (magical tome)
    const book = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.3, 1),
      new THREE.MeshStandardMaterial({
        color: 0x4a2a1a,
        emissive: 0x221100,
        emissiveIntensity: 0.3,
      })
    );
    book.position.set(x, 15, z);
    book.rotation.z = 0.1;
    this.scene.add(book);
  }

  // === BLOOD ALTAR (Diablo) ===
  private createBloodAltar(x: number, z: number): void {
    // Corrupted ground
    const corruptGround = new THREE.Mesh(
      new THREE.CircleGeometry(12, 32),
      new THREE.MeshStandardMaterial({
        color: 0x2a0505,
        emissive: 0x1a0000,
        emissiveIntensity: 0.2,
      })
    );
    corruptGround.rotation.x = -Math.PI / 2;
    corruptGround.position.set(x, 0.02, z);
    this.scene.add(corruptGround);

    // Stone altar
    const altar = new THREE.Mesh(
      new THREE.BoxGeometry(5, 2, 3),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
    );
    altar.position.set(x, 1, z);
    this.scene.add(altar);

    // Blood pool on altar
    const blood = new THREE.Mesh(
      new THREE.PlaneGeometry(4, 2.5),
      new THREE.MeshStandardMaterial({
        color: 0x660000,
        emissive: 0x330000,
        emissiveIntensity: 0.5,
      })
    );
    blood.rotation.x = -Math.PI / 2;
    blood.position.set(x, 2.01, z);
    this.scene.add(blood);

    // Demonic pillars
    [-6, 6].forEach((dx) => {
      const pillar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.8, 1, 6, 6),
        new THREE.MeshStandardMaterial({ color: 0x1a1a1a })
      );
      pillar.position.set(x + dx, 3, z);
      this.scene.add(pillar);

      // Skull on top
      const skull = new THREE.Mesh(
        new THREE.SphereGeometry(0.6, 6, 5),
        new THREE.MeshStandardMaterial({ color: 0xaa9988 })
      );
      skull.position.set(x + dx, 6.5, z);
      skull.scale.z = 0.7;
      this.scene.add(skull);

      // Red eye glow
      const eyeLight = new THREE.PointLight(0xff0000, 0.5, 8);
      eyeLight.position.set(x + dx, 6.5, z + 1);
      this.scene.add(eyeLight);
    });

    // Chains
    [-3, 3].forEach((dx) => {
      const chain = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, 0.1, 4, 6),
        new THREE.MeshStandardMaterial({ color: 0x3a3a3a })
      );
      chain.position.set(x + dx, 4, z - 2);
      chain.rotation.z = dx > 0 ? 0.3 : -0.3;
      this.scene.add(chain);
    });

    // Ominous red glow
    const altarLight = new THREE.PointLight(0xff2200, 1.5, 15);
    altarLight.position.set(x, 3, z);
    this.scene.add(altarLight);
  }

  // === TOWN WALLS ===
  private createTownWalls(): void {
    const wallColor = 0x5a5a5a;
    const wallHeight = 6;
    const wallThickness = 2;
    const townRadius = 35;

    // Create wall sections with gates on each cardinal direction
    const wallSections = [
      // North wall (with gap for gate)
      { x1: -townRadius, z1: townRadius, x2: -8, z2: townRadius },
      { x1: 8, z1: townRadius, x2: townRadius, z2: townRadius },
      // South wall
      { x1: -townRadius, z1: -townRadius, x2: -8, z2: -townRadius },
      { x1: 8, z1: -townRadius, x2: townRadius, z2: -townRadius },
      // East wall
      { x1: townRadius, z1: -townRadius, x2: townRadius, z2: -8 },
      { x1: townRadius, z1: 8, x2: townRadius, z2: townRadius },
      // West wall
      { x1: -townRadius, z1: -townRadius, x2: -townRadius, z2: -8 },
      { x1: -townRadius, z1: 8, x2: -townRadius, z2: townRadius },
    ];

    wallSections.forEach((w) => {
      const length = Math.sqrt((w.x2 - w.x1) ** 2 + (w.z2 - w.z1) ** 2);
      const angle = Math.atan2(w.z2 - w.z1, w.x2 - w.x1);

      const wall = new THREE.Mesh(
        new THREE.BoxGeometry(length, wallHeight, wallThickness),
        new THREE.MeshStandardMaterial({ color: wallColor, roughness: 0.9 })
      );
      wall.position.set((w.x1 + w.x2) / 2, wallHeight / 2, (w.z1 + w.z2) / 2);
      wall.rotation.y = -angle;
      wall.castShadow = true;
      this.scene.add(wall);
    });

    // Corner towers
    const towerPositions = [
      { x: townRadius, z: townRadius },
      { x: -townRadius, z: townRadius },
      { x: townRadius, z: -townRadius },
      { x: -townRadius, z: -townRadius },
    ];

    towerPositions.forEach((t) => {
      // Tower body
      const tower = new THREE.Mesh(
        new THREE.CylinderGeometry(4, 5, 10, 8),
        new THREE.MeshStandardMaterial({ color: wallColor })
      );
      tower.position.set(t.x, 5, t.z);
      tower.castShadow = true;
      this.scene.add(tower);

      // Tower top
      const towerTop = new THREE.Mesh(
        new THREE.ConeGeometry(5, 4, 8),
        new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
      );
      towerTop.position.set(t.x, 12, t.z);
      this.scene.add(towerTop);

      // Torch on tower
      const torch = new THREE.PointLight(0xff8844, 1, 15);
      torch.position.set(t.x, 8, t.z);
      this.scene.add(torch);
    });

    // Gate arches
    const gatePositions = [
      { x: 0, z: townRadius, ry: 0 },
      { x: 0, z: -townRadius, ry: 0 },
      { x: townRadius, z: 0, ry: Math.PI / 2 },
      { x: -townRadius, z: 0, ry: Math.PI / 2 },
    ];

    gatePositions.forEach((g) => {
      // Gate pillars
      [-6, 6].forEach((offset) => {
        const pillar = new THREE.Mesh(
          new THREE.BoxGeometry(2, 8, 2),
          new THREE.MeshStandardMaterial({ color: wallColor })
        );
        if (g.ry === 0) {
          pillar.position.set(g.x + offset, 4, g.z);
        } else {
          pillar.position.set(g.x, 4, g.z + offset);
        }
        this.scene.add(pillar);
      });

      // Gate arch
      const arch = new THREE.Mesh(
        new THREE.BoxGeometry(g.ry === 0 ? 14 : 2, 2, g.ry === 0 ? 2 : 14),
        new THREE.MeshStandardMaterial({ color: wallColor })
      );
      arch.position.set(g.x, 9, g.z);
      this.scene.add(arch);
    });
  }

  private createCrystalCave(x: number, z: number): void {
    // Cave entrance (dark rock formation)
    const caveEntrance = new THREE.Mesh(
      new THREE.SphereGeometry(8, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.95, side: THREE.DoubleSide })
    );
    caveEntrance.position.set(x, 0, z);
    caveEntrance.scale.set(1.5, 1, 1);
    this.scene.add(caveEntrance);

    // Crystals around entrance
    const crystalColors = [0x88ffff, 0xff88ff, 0x8888ff, 0xffff88];
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const radius = 6 + Math.random() * 4;
      const crystal = new THREE.Mesh(
        new THREE.ConeGeometry(0.3 + Math.random() * 0.3, 1 + Math.random() * 2, 6),
        new THREE.MeshStandardMaterial({
          color: crystalColors[i % crystalColors.length],
          emissive: crystalColors[i % crystalColors.length],
          emissiveIntensity: 0.3,
          transparent: true,
          opacity: 0.8,
        })
      );
      crystal.position.set(
        x + Math.cos(angle) * radius,
        0.5 + Math.random() * 1,
        z + Math.sin(angle) * radius
      );
      crystal.rotation.set(Math.random() * 0.3, 0, Math.random() * 0.3);
      this.scene.add(crystal);
    }

    // Glowing crystal inside cave
    const bigCrystal = new THREE.Mesh(
      new THREE.ConeGeometry(1, 4, 6),
      new THREE.MeshStandardMaterial({
        color: 0x00ffff,
        emissive: 0x00ffff,
        emissiveIntensity: 0.5,
      })
    );
    bigCrystal.position.set(x, 2, z + 3);
    this.scene.add(bigCrystal);

    // Point light for glow effect
    const crystalLight = new THREE.PointLight(0x00ffff, 1, 20);
    crystalLight.position.set(x, 3, z + 3);
    this.scene.add(crystalLight);
  }

  private createWaterfall(x: number, z: number): void {
    // Cliff
    const cliff = new THREE.Mesh(
      new THREE.BoxGeometry(15, 12, 8),
      new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.9 })
    );
    cliff.position.set(x, 6, z - 4);
    cliff.castShadow = true;
    this.scene.add(cliff);

    // Waterfall (animated blue plane)
    const waterfall = new THREE.Mesh(
      new THREE.PlaneGeometry(4, 12),
      new THREE.MeshStandardMaterial({
        color: 0x4488cc,
        transparent: true,
        opacity: 0.7,
        emissive: 0x224466,
        emissiveIntensity: 0.2,
      })
    );
    waterfall.position.set(x, 6, z);
    waterfall.castShadow = true;
    this.scene.add(waterfall);

    // Pond at base
    const pond = new THREE.Mesh(
      new THREE.CircleGeometry(12, 32),
      new THREE.MeshStandardMaterial({
        color: 0x3377aa,
        transparent: true,
        opacity: 0.8,
        metalness: 0.3,
        roughness: 0.1,
      })
    );
    pond.rotation.x = -Math.PI / 2;
    pond.position.set(x, 0.1, z + 8);
    this.scene.add(pond);

    // Mist particles (simple spheres)
    for (let i = 0; i < 8; i++) {
      const mist = new THREE.Mesh(
        new THREE.SphereGeometry(1 + Math.random(), 8, 8),
        new THREE.MeshStandardMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.2,
        })
      );
      mist.position.set(x + (Math.random() - 0.5) * 6, 1 + Math.random() * 2, z + 5 + Math.random() * 3);
      this.scene.add(mist);
    }

    // Rocks around pond
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const rock = new THREE.Mesh(
        new THREE.DodecahedronGeometry(1 + Math.random()),
        new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.9 })
      );
      rock.position.set(x + Math.cos(angle) * 13, 0.5, z + 8 + Math.sin(angle) * 13);
      rock.castShadow = true;
      this.scene.add(rock);
    }
  }

  private createStoneCircle(x: number, z: number): void {
    const stoneCount = 12;
    const radius = 15;

    // Standing stones
    for (let i = 0; i < stoneCount; i++) {
      const angle = (i / stoneCount) * Math.PI * 2;
      const height = 4 + Math.random() * 3;
      const stone = new THREE.Mesh(
        new THREE.BoxGeometry(1.5, height, 0.8),
        new THREE.MeshStandardMaterial({ color: 0x555566, roughness: 0.95 })
      );
      stone.position.set(x + Math.cos(angle) * radius, height / 2, z + Math.sin(angle) * radius);
      stone.rotation.y = angle + Math.PI / 2;
      stone.rotation.z = (Math.random() - 0.5) * 0.1;
      stone.castShadow = true;
      this.scene.add(stone);
    }

    // Central altar
    const altar = new THREE.Mesh(
      new THREE.CylinderGeometry(3, 3.5, 1.5, 8),
      new THREE.MeshStandardMaterial({ color: 0x444455, roughness: 0.8 })
    );
    altar.position.set(x, 0.75, z);
    altar.castShadow = true;
    this.scene.add(altar);

    // Glowing runes on altar
    const runeLight = new THREE.PointLight(0x8844ff, 0.8, 25);
    runeLight.position.set(x, 2, z);
    this.scene.add(runeLight);

    // Rune circle
    const runeCircle = new THREE.Mesh(
      new THREE.RingGeometry(2, 2.8, 32),
      new THREE.MeshStandardMaterial({
        color: 0x8844ff,
        emissive: 0x8844ff,
        emissiveIntensity: 0.5,
        side: THREE.DoubleSide,
      })
    );
    runeCircle.rotation.x = -Math.PI / 2;
    runeCircle.position.set(x, 1.51, z);
    this.scene.add(runeCircle);
  }

  private createWatchtower(x: number, z: number): void {
    // Tower base
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(4, 5, 2, 8),
      new THREE.MeshStandardMaterial({ color: 0x6a5a4a, roughness: 0.9 })
    );
    base.position.set(x, 1, z);
    base.castShadow = true;
    this.scene.add(base);

    // Tower body
    const tower = new THREE.Mesh(
      new THREE.CylinderGeometry(3, 4, 15, 8),
      new THREE.MeshStandardMaterial({ color: 0x7a6a5a, roughness: 0.85 })
    );
    tower.position.set(x, 9.5, z);
    tower.castShadow = true;
    this.scene.add(tower);

    // Broken top section
    const brokenTop = new THREE.Mesh(
      new THREE.CylinderGeometry(2.5, 3, 4, 8, 1, false, 0, Math.PI * 1.5),
      new THREE.MeshStandardMaterial({ color: 0x7a6a5a, roughness: 0.85, side: THREE.DoubleSide })
    );
    brokenTop.position.set(x, 19, z);
    brokenTop.castShadow = true;
    this.scene.add(brokenTop);

    // Wooden platform remains
    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(3.5, 3.5, 0.3, 8),
      new THREE.MeshStandardMaterial({ color: 0x5a4a3a })
    );
    platform.position.set(x, 17, z);
    this.scene.add(platform);

    // Fallen debris
    for (let i = 0; i < 5; i++) {
      const debris = new THREE.Mesh(
        new THREE.BoxGeometry(1 + Math.random(), 0.5, 1 + Math.random()),
        new THREE.MeshStandardMaterial({ color: 0x6a5a4a })
      );
      debris.position.set(x + (Math.random() - 0.5) * 10, 0.25, z + (Math.random() - 0.5) * 10);
      debris.rotation.y = Math.random() * Math.PI;
      this.scene.add(debris);
    }
  }

  private createMagicalGrove(x: number, z: number): void {
    // Giant magical tree in center
    const giantTrunk = new THREE.Mesh(
      new THREE.CylinderGeometry(2, 3, 12, 8),
      new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
    );
    giantTrunk.position.set(x, 6, z);
    giantTrunk.castShadow = true;
    this.scene.add(giantTrunk);

    // Glowing foliage
    const giantFoliage = new THREE.Mesh(
      new THREE.SphereGeometry(10, 16, 12),
      new THREE.MeshStandardMaterial({
        color: 0x88ffaa,
        emissive: 0x44aa66,
        emissiveIntensity: 0.3,
      })
    );
    giantFoliage.position.set(x, 16, z);
    giantFoliage.scale.y = 0.7;
    giantFoliage.castShadow = true;
    this.scene.add(giantFoliage);

    // Magical light
    const treeLight = new THREE.PointLight(0x88ffaa, 1.5, 40);
    treeLight.position.set(x, 12, z);
    this.scene.add(treeLight);

    // Smaller magical trees around
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const radius = 12 + Math.random() * 5;
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.4, 0.6, 4, 6),
        new THREE.MeshStandardMaterial({ color: 0x5a4a3a })
      );
      trunk.position.set(x + Math.cos(angle) * radius, 2, z + Math.sin(angle) * radius);
      this.scene.add(trunk);

      const foliage = new THREE.Mesh(
        new THREE.SphereGeometry(2, 8, 6),
        new THREE.MeshStandardMaterial({
          color: 0x66ddaa,
          emissive: 0x338855,
          emissiveIntensity: 0.2,
        })
      );
      foliage.position.set(x + Math.cos(angle) * radius, 5, z + Math.sin(angle) * radius);
      this.scene.add(foliage);
    }

    // Floating particles (fireflies)
    for (let i = 0; i < 20; i++) {
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(0.1),
        new THREE.MeshStandardMaterial({
          color: 0xffffaa,
          emissive: 0xffff66,
          emissiveIntensity: 1,
        })
      );
      particle.position.set(
        x + (Math.random() - 0.5) * 30,
        2 + Math.random() * 15,
        z + (Math.random() - 0.5) * 30
      );
      this.scene.add(particle);
    }
  }

  private createBridge(x: number, z: number): void {
    // Ravine (darker ground)
    const ravine = new THREE.Mesh(
      new THREE.BoxGeometry(8, 4, 40),
      new THREE.MeshStandardMaterial({ color: 0x1a1a1a })
    );
    ravine.position.set(x, -2, z);
    this.scene.add(ravine);

    // Bridge planks
    const bridgeBase = new THREE.Mesh(
      new THREE.BoxGeometry(6, 0.5, 12),
      new THREE.MeshStandardMaterial({ color: 0x8b7355 })
    );
    bridgeBase.position.set(x, 0.25, z);
    bridgeBase.castShadow = true;
    this.scene.add(bridgeBase);

    // Bridge railings
    for (let side = -1; side <= 1; side += 2) {
      for (let i = -5; i <= 5; i += 2) {
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.15, 0.15, 2, 6),
          new THREE.MeshStandardMaterial({ color: 0x6a5a4a })
        );
        post.position.set(x + side * 2.8, 1.25, z + i);
        this.scene.add(post);
      }

      // Railing beam
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(0.2, 0.2, 12),
        new THREE.MeshStandardMaterial({ color: 0x6a5a4a })
      );
      rail.position.set(x + side * 2.8, 2.1, z);
      this.scene.add(rail);
    }

    // Support beams underneath
    const support1 = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 3, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x5a4a3a })
    );
    support1.position.set(x - 2, -1, z - 4);
    support1.rotation.z = 0.2;
    this.scene.add(support1);

    const support2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 3, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x5a4a3a })
    );
    support2.position.set(x + 2, -1, z + 4);
    support2.rotation.z = -0.2;
    this.scene.add(support2);
  }

  private createCampsite(x: number, z: number): void {
    // Campfire
    const firepit = new THREE.Mesh(
      new THREE.CylinderGeometry(1.2, 1.5, 0.3, 8),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a })
    );
    firepit.position.set(x, 0.15, z);
    this.scene.add(firepit);

    // Fire (glowing cone)
    const fire = new THREE.Mesh(
      new THREE.ConeGeometry(0.6, 1.5, 8),
      new THREE.MeshStandardMaterial({
        color: 0xff6600,
        emissive: 0xff4400,
        emissiveIntensity: 1,
      })
    );
    fire.position.set(x, 1, z);
    this.scene.add(fire);

    // Fire light
    const fireLight = new THREE.PointLight(0xff6600, 2, 15);
    fireLight.position.set(x, 2, z);
    this.scene.add(fireLight);

    // Tents
    const tentPositions = [
      { dx: -6, dz: -3, rotation: 0.3 },
      { dx: 5, dz: -4, rotation: -0.4 },
      { dx: -4, dz: 5, rotation: 0.8 },
    ];

    tentPositions.forEach((t) => {
      // Tent body
      const tent = new THREE.Mesh(
        new THREE.ConeGeometry(2.5, 3, 4),
        new THREE.MeshStandardMaterial({ color: 0x8b7355 })
      );
      tent.position.set(x + t.dx, 1.5, z + t.dz);
      tent.rotation.y = t.rotation;
      tent.castShadow = true;
      this.scene.add(tent);
    });

    // Logs for sitting
    for (let i = 0; i < 3; i++) {
      const angle = (i / 3) * Math.PI * 2 + 0.5;
      const log = new THREE.Mesh(
        new THREE.CylinderGeometry(0.3, 0.35, 2, 6),
        new THREE.MeshStandardMaterial({ color: 0x5a4030 })
      );
      log.position.set(x + Math.cos(angle) * 3, 0.3, z + Math.sin(angle) * 3);
      log.rotation.z = Math.PI / 2;
      log.rotation.y = angle;
      this.scene.add(log);
    }

    // Cooking pot
    const pot = new THREE.Mesh(
      new THREE.SphereGeometry(0.5, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, side: THREE.DoubleSide })
    );
    pot.position.set(x + 1, 0.8, z);
    pot.rotation.x = Math.PI;
    this.scene.add(pot);
  }

  private createGraveyard(x: number, z: number): void {
    // Fence posts
    for (let i = -20; i <= 20; i += 4) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 2, 0.3),
        new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
      );
      post.position.set(x + i, 1, z - 15);
      this.scene.add(post);

      const post2 = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 2, 0.3),
        new THREE.MeshStandardMaterial({ color: 0x2a2a2a })
      );
      post2.position.set(x + i, 1, z + 15);
      this.scene.add(post2);
    }

    // Gravestones
    const gravePositions = [
      { dx: -12, dz: -8 }, { dx: -6, dz: -8 }, { dx: 0, dz: -8 }, { dx: 6, dz: -8 }, { dx: 12, dz: -8 },
      { dx: -9, dz: -2 }, { dx: -3, dz: -2 }, { dx: 3, dz: -2 }, { dx: 9, dz: -2 },
      { dx: -12, dz: 4 }, { dx: -6, dz: 4 }, { dx: 0, dz: 4 }, { dx: 6, dz: 4 }, { dx: 12, dz: 4 },
      { dx: -9, dz: 10 }, { dx: -3, dz: 10 }, { dx: 3, dz: 10 }, { dx: 9, dz: 10 },
    ];

    gravePositions.forEach((g) => {
      const height = 1.2 + Math.random() * 0.8;
      const gravestone = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, height, 0.3),
        new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.95 })
      );
      gravestone.position.set(x + g.dx, height / 2, z + g.dz);
      gravestone.rotation.z = (Math.random() - 0.5) * 0.15;
      gravestone.castShadow = true;
      this.scene.add(gravestone);

      // Rounded top
      const top = new THREE.Mesh(
        new THREE.SphereGeometry(0.4, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.95 })
      );
      top.position.set(x + g.dx, height, z + g.dz);
      top.rotation.z = gravestone.rotation.z;
      this.scene.add(top);
    });

    // Creepy dead tree
    const deadTrunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.8, 6, 6),
      new THREE.MeshStandardMaterial({ color: 0x2a2015 })
    );
    deadTrunk.position.set(x - 15, 3, z);
    this.scene.add(deadTrunk);

    // Bare branches
    for (let i = 0; i < 4; i++) {
      const branch = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, 0.2, 3, 4),
        new THREE.MeshStandardMaterial({ color: 0x2a2015 })
      );
      branch.position.set(x - 15, 5, z);
      branch.rotation.z = Math.PI / 3;
      branch.rotation.y = (i / 4) * Math.PI * 2;
      this.scene.add(branch);
    }

    // Fog ground cover
    const fog = new THREE.Mesh(
      new THREE.PlaneGeometry(50, 35),
      new THREE.MeshStandardMaterial({
        color: 0x888899,
        transparent: true,
        opacity: 0.3,
      })
    );
    fog.rotation.x = -Math.PI / 2;
    fog.position.set(x, 0.3, z);
    this.scene.add(fog);

    // Eerie light
    const eerieLight = new THREE.PointLight(0x6666aa, 0.5, 40);
    eerieLight.position.set(x, 3, z);
    this.scene.add(eerieLight);
  }

  private createCaravan(x: number, z: number): void {
    // Main wagon
    const wagonBody = new THREE.Mesh(
      new THREE.BoxGeometry(6, 3, 3),
      new THREE.MeshStandardMaterial({ color: 0x8b6914 })
    );
    wagonBody.position.set(x, 2, z);
    wagonBody.castShadow = true;
    this.scene.add(wagonBody);

    // Wagon roof
    const roof = new THREE.Mesh(
      new THREE.BoxGeometry(6.5, 0.3, 3.5),
      new THREE.MeshStandardMaterial({ color: 0x5a4a2a })
    );
    roof.position.set(x, 3.65, z);
    this.scene.add(roof);

    // Wagon canvas top
    const canvas = new THREE.Mesh(
      new THREE.CylinderGeometry(1.8, 1.8, 6.5, 8, 1, false, 0, Math.PI),
      new THREE.MeshStandardMaterial({ color: 0xccbb99, side: THREE.DoubleSide })
    );
    canvas.position.set(x, 4.5, z);
    canvas.rotation.z = Math.PI / 2;
    canvas.rotation.y = Math.PI / 2;
    this.scene.add(canvas);

    // Wheels
    const wheelPositions = [
      { dx: -2, dz: -1.8 }, { dx: -2, dz: 1.8 },
      { dx: 2, dz: -1.8 }, { dx: 2, dz: 1.8 },
    ];

    wheelPositions.forEach((w) => {
      const wheel = new THREE.Mesh(
        new THREE.TorusGeometry(0.8, 0.15, 8, 16),
        new THREE.MeshStandardMaterial({ color: 0x4a3a2a })
      );
      wheel.position.set(x + w.dx, 0.8, z + w.dz);
      wheel.rotation.y = Math.PI / 2;
      this.scene.add(wheel);

      // Wheel spokes
      const hub = new THREE.Mesh(
        new THREE.CylinderGeometry(0.15, 0.15, 0.3, 8),
        new THREE.MeshStandardMaterial({ color: 0x3a2a1a })
      );
      hub.position.set(x + w.dx, 0.8, z + w.dz);
      hub.rotation.x = Math.PI / 2;
      this.scene.add(hub);
    });

    // Goods crates
    const crate1 = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 1, 1.2),
      new THREE.MeshStandardMaterial({ color: 0x7a5a3a })
    );
    crate1.position.set(x + 5, 0.5, z - 2);
    this.scene.add(crate1);

    const crate2 = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.8, 1),
      new THREE.MeshStandardMaterial({ color: 0x6a4a2a })
    );
    crate2.position.set(x + 5.5, 0.4, z + 1.5);
    this.scene.add(crate2);

    // Barrel
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.6, 0.6, 1.2, 12),
      new THREE.MeshStandardMaterial({ color: 0x5a4530 })
    );
    barrel.position.set(x - 5, 0.6, z + 1);
    this.scene.add(barrel);

    // Lantern on wagon
    const lantern = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.5, 0.3),
      new THREE.MeshStandardMaterial({
        color: 0xffaa00,
        emissive: 0xff8800,
        emissiveIntensity: 0.8,
      })
    );
    lantern.position.set(x + 3.2, 3.2, z - 1.5);
    this.scene.add(lantern);

    // Lantern light
    const lanternLight = new THREE.PointLight(0xffaa00, 0.8, 10);
    lanternLight.position.set(x + 3.2, 3.2, z - 1.5);
    this.scene.add(lanternLight);
  }

  private spawnEnemies(): void {
    // Slimes outside town walls (Level 1-2) - town radius is 35
    const slimePositions = [
      { x: 40, z: 10, level: 1 },
      { x: 42, z: -5, level: 1 },
      { x: 38, z: 20, level: 1 },
      { x: -40, z: 12, level: 2 },
      { x: -42, z: -8, level: 2 },
      { x: 45, z: 15, level: 2 },
    ];

    const slimeColors = [0x8b0000, 0x006400, 0x00008b, 0x8b008b, 0x008b8b, 0x8b8b00];

    slimePositions.forEach((pos, index) => {
      const enemy = new Enemy({
        scene: this.scene,
        position: new THREE.Vector3(pos.x, 0, pos.z),
        name: `Slime`,
        level: pos.level,
        maxHealth: 40 + pos.level * 20,
        color: slimeColors[index % slimeColors.length],
      });
      this.enemies.push(enemy);
    });

    // Wolves in the forest area (Level 3-4) - further outside town
    const wolfPositions = [
      { x: 50, z: 45, level: 3 },
      { x: 55, z: 40, level: 3 },
      { x: 48, z: 52, level: 4 },
      { x: -50, z: 45, level: 3 },
      { x: -55, z: 50, level: 4 },
    ];

    wolfPositions.forEach((pos) => {
      const enemy = new Enemy({
        scene: this.scene,
        position: new THREE.Vector3(pos.x, 0, pos.z),
        name: `Wolf`,
        level: pos.level,
        maxHealth: 80 + pos.level * 25,
        color: 0x4a4a4a,
      });
      this.enemies.push(enemy);
    });

    // Goblins near the cave entrance (Level 4-6) - outside town walls
    const goblinPositions = [
      { x: -50, z: -40, level: 4 },
      { x: -55, z: -38, level: 5 },
      { x: -52, z: -45, level: 5 },
      { x: -58, z: -42, level: 6 },
    ];

    goblinPositions.forEach((pos) => {
      const enemy = new Enemy({
        scene: this.scene,
        position: new THREE.Vector3(pos.x, 0, pos.z),
        name: `Goblin`,
        level: pos.level,
        maxHealth: 100 + pos.level * 30,
        color: 0x228b22,
      });
      this.enemies.push(enemy);
    });

    // Elite spider boss (Level 8) - deep in the cave area
    const boss = new Enemy({
      scene: this.scene,
      position: new THREE.Vector3(-65, 0, -50),
      name: `Giant Spider`,
      level: 8,
      maxHealth: 500,
      color: 0x1a1a1a,
    });
    this.enemies.push(boss);

    // Add nameplates to all enemies
    this.enemies.forEach(enemy => {
      this.nameplateSystem.addEnemy(enemy);

      // Add to optimized nameplate system
      this.optimizedNameplates.addEntity({
        id: enemy.id,
        name: enemy.name,
        level: enemy.level,
        health: enemy.health,
        maxHealth: enemy.maxHealth,
        position: enemy.mesh.position,
        isHostile: true,
        isNPC: false,
        isTargeted: false,
        isDead: false,
      });

      // Add to spatial tree
      this.spatialTree.insert({
        id: enemy.id,
        position: enemy.mesh.position,
        radius: 1,
      });
    });

    // Update targeting system with enemies
    this.targetingSystem.setEnemies(this.enemies);
  }

  private spawnNPCs(): void {
    // Village Elder - Quest giver
    const elder = new NPC({
      scene: this.scene,
      position: new THREE.Vector3(-3, 0, -3),
      id: 'village_elder',
      name: 'Village Elder',
      type: 'quest_giver',
    });
    this.npcs.push(elder);

    // General Merchant - Vendor
    const merchant = new NPC({
      scene: this.scene,
      position: new THREE.Vector3(5, 0, -5),
      id: 'general_merchant',
      name: 'General Merchant',
      type: 'vendor',
    });
    this.npcs.push(merchant);

    // Blacksmith - Vendor
    const blacksmith = new NPC({
      scene: this.scene,
      position: new THREE.Vector3(8, 0, -3),
      id: 'weaponsmith',
      name: 'Marcus the Blacksmith',
      type: 'vendor',
    });
    this.npcs.push(blacksmith);

    // Innkeeper
    const innkeeper = new NPC({
      scene: this.scene,
      position: new THREE.Vector3(-8, 0, 2),
      id: 'innkeeper',
      name: 'Helena the Innkeeper',
      type: 'vendor',
    });
    this.npcs.push(innkeeper);

    // Update quest markers
    this.updateNPCQuestMarkers();
  }

  private onCanvasMouseDown(event: MouseEvent): void {
    // Track mouse position on mousedown to detect drag vs click
    this.mouseDownPos = { x: event.clientX, y: event.clientY };
  }

  private isClick(event: MouseEvent): boolean {
    // Check if mouse moved significantly since mousedown (if so, it's a drag, not a click)
    if (!this.mouseDownPos) return true;
    const dx = Math.abs(event.clientX - this.mouseDownPos.x);
    const dy = Math.abs(event.clientY - this.mouseDownPos.y);
    return dx < this.CLICK_THRESHOLD && dy < this.CLICK_THRESHOLD;
  }

  private onCanvasClick(event: MouseEvent): void {
    // Don't process clicks if dialog is open
    if (this.dialogUI.isDialogOpen()) return;
    if (this.inventorySystem.isInventoryOpen()) return;

    // Left-click is used for camera rotation - only select if it was a quick click, not a drag
    if (event.button === 0 && this.isClick(event)) {
      // Left-click: Target enemy or NPC (selection only, no combat)

      // Check for NPC click first (friendly targets)
      const hitNPC = this.targetingSystem.raycastNPCFromMouse(
        event.clientX,
        event.clientY,
        this.npcs
      );

      if (hitNPC) {
        // Clear enemy target and select NPC
        this.targetingSystem.clearTarget();
        this.stopAutoAttack();
        this.selectedNPC = hitNPC;

        // Show NPC in target frame (level 0 = friendly, full health for display)
        this.hud.showTarget(hitNPC.name, 0, 100, 100);
        return;
      }

      // Check for enemy click
      const hitEnemy = this.targetingSystem.raycastFromMouse(
        event.clientX,
        event.clientY,
        this.enemies
      );

      if (hitEnemy) {
        // Clear NPC selection
        this.selectedNPC = null;

        this.targetingSystem.setTarget(hitEnemy);
        this.hud.showTarget(hitEnemy.name, hitEnemy.level, hitEnemy.health, hitEnemy.maxHealth);
        // Don't start auto-attack on left-click - just select
      } else {
        // Clicked on nothing - clear all targets
        this.targetingSystem.clearTarget();
        this.selectedNPC = null;
        this.hud.hideTarget();
        this.stopAutoAttack();
      }
    }
  }

  private onCanvasRightClick(event: MouseEvent): void {
    event.preventDefault(); // Prevent context menu

    // Don't process clicks if dialog is open
    if (this.dialogUI.isDialogOpen()) return;
    if (this.inventorySystem.isInventoryOpen()) return;

    // Check if clicked on NPC first (interaction takes priority)
    const hitNPC = this.targetingSystem.raycastNPCFromMouse(
      event.clientX,
      event.clientY,
      this.npcs
    );

    if (hitNPC) {
      // Clear any enemy target and select this NPC
      this.targetingSystem.clearTarget();
      this.stopAutoAttack();
      this.selectedNPC = hitNPC;
      this.hud.showTarget(hitNPC.name, 0, 100, 100);

      // Move towards NPC and set pending interaction
      this.player.setMoveTarget(hitNPC.mesh.position);
      this.pendingNPCInteraction = hitNPC;

      // If already in range, interact immediately
      const playerPos = this.player.getPositionRef();
      if (hitNPC.isInRange(playerPos)) {
        this.pendingNPCInteraction = null;
        this.interactWithNPC(hitNPC);
      }
      return;
    }

    // If we have a selected NPC and right-click elsewhere, move to and interact with it
    if (this.selectedNPC) {
      this.player.setMoveTarget(this.selectedNPC.mesh.position);
      this.pendingNPCInteraction = this.selectedNPC;

      // If already in range, interact immediately
      const playerPos = this.player.getPositionRef();
      if (this.selectedNPC.isInRange(playerPos)) {
        this.pendingNPCInteraction = null;
        this.interactWithNPC(this.selectedNPC);
      }
      return;
    }

    // Check if clicked on enemy (right-click to move towards and attack)
    const hitEnemy = this.targetingSystem.raycastFromMouse(
      event.clientX,
      event.clientY,
      this.enemies
    );

    if (hitEnemy) {
      // Target the enemy
      this.targetingSystem.setTarget(hitEnemy);
      this.hud.showTarget(hitEnemy.name, hitEnemy.level, hitEnemy.health, hitEnemy.maxHealth);

      // Move towards enemy
      this.player.setMoveTarget(hitEnemy.mesh.position);

      // Start auto-attack (will attack when in range)
      this.startAutoAttack();
      return;
    }

    // Right-click on ground: move to location
    if (this.groundPlane) {
      this._mouseVec2.set(
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1
      );

      this._groundRaycaster.setFromCamera(this._mouseVec2, this.camera);
      const intersects = this._groundRaycaster.intersectObject(this.groundPlane);

      if (intersects.length > 0) {
        const targetPoint = intersects[0].point;
        this.player.setMoveTarget(targetPoint);

        // Clear target when moving to empty ground
        this.targetingSystem.clearTarget();
        this.hud.hideTarget();
        this.stopAutoAttack();
        this.selectedNPC = null;
        this.pendingNPCInteraction = null;
      }
    }
  }

  private onEnemyKilled(enemy: Enemy): void {
    // Stop auto-attacking this target
    this.stopAutoAttack();

    // Update quest progress based on enemy type
    const enemyType = enemy.name.toLowerCase().replace(' ', '_');
    this.questSystem.updateKillProgress(enemyType);

    // XP scales with enemy level
    const xpAmount = 15 + (enemy.level * 10) + Math.floor(enemy.maxHealth / 10);
    const pos = enemy.mesh.position.clone();
    pos.y += 2.5;
    this.floatingText.showXP(xpAmount, pos);

    // Log experience gain
    this.combatLog.logExperience(xpAmount, enemy.name);

    // Play death sound
    this.audioSystem.play('enemyDeath');

    // Drop loot on ground
    this.dropLoot(enemy.mesh.position.clone(), enemy);

    // Grant experience
    this.hud.addExperience(xpAmount);

    // Remove nameplate
    this.nameplateSystem.removeEntity(enemy.id);
  }

  private dropLoot(position: THREE.Vector3, enemy?: Enemy): void {
    // Enemy-specific loot tables
    const lootTables: Record<string, { itemId: string; chance: number; min: number; max: number }[]> = {
      slime: [
        { itemId: 'slime_goo', chance: 0.8, min: 1, max: 3 },
        { itemId: 'slime_core', chance: 0.2, min: 1, max: 1 },
        { itemId: 'health_potion', chance: 0.25, min: 1, max: 1 },
      ],
      wolf: [
        { itemId: 'wolf_pelt', chance: 0.7, min: 1, max: 2 },
        { itemId: 'wolf_fang', chance: 0.4, min: 1, max: 2 },
        { itemId: 'health_potion', chance: 0.3, min: 1, max: 1 },
        { itemId: 'leather_scraps', chance: 0.5, min: 1, max: 3 },
      ],
      goblin: [
        { itemId: 'goblin_ear', chance: 0.6, min: 1, max: 2 },
        { itemId: 'rusty_dagger', chance: 0.15, min: 1, max: 1 },
        { itemId: 'health_potion', chance: 0.35, min: 1, max: 2 },
        { itemId: 'mana_potion', chance: 0.25, min: 1, max: 1 },
      ],
      'giant spider': [
        { itemId: 'spider_silk', chance: 0.8, min: 2, max: 5 },
        { itemId: 'spider_venom', chance: 0.5, min: 1, max: 2 },
        { itemId: 'health_potion', chance: 0.5, min: 1, max: 2 },
        { itemId: 'mana_potion', chance: 0.4, min: 1, max: 2 },
        { itemId: 'iron_sword', chance: 0.1, min: 1, max: 1 }, // Rare drop from boss
      ],
    };

    // Get loot table based on enemy type, fallback to slime
    const enemyType = enemy?.name.toLowerCase() || 'slime';
    const drops = lootTables[enemyType] || lootTables['slime'];

    drops.forEach((drop) => {
      if (Math.random() < drop.chance) {
        const quantity = Math.floor(Math.random() * (drop.max - drop.min + 1)) + drop.min;

        // Check if item exists in ITEMS
        if (!ITEMS[drop.itemId]) return;

        const loot = new LootDrop({
          scene: this.scene,
          position: position.clone().add(
            new THREE.Vector3(
              (Math.random() - 0.5) * 2,
              0,
              (Math.random() - 0.5) * 2
            )
          ),
          itemId: drop.itemId,
          quantity,
        });
        this.lootDrops.push(loot);

        // Log loot drop
        const itemDef = ITEMS[drop.itemId];
        this.combatLog.logLoot(itemDef.name, quantity);
      }
    });

    // Gold scales with enemy level
    const enemyLevel = enemy?.level || 1;
    const baseGold = 3 + enemyLevel * 2;
    const goldAmount = Math.floor(Math.random() * baseGold) + baseGold;
    this.inventorySystem.addGold(goldAmount);
    this.hud.setGold(this.inventorySystem.getGold());
    this.hud.showNotification(`+${goldAmount} Gold`, '#ffd700');
  }

  private onKeyDown(event: KeyboardEvent): void {
    // Don't process game keys if chat is open
    if (this.chatUI.isChatOpen()) return;

    // F key to interact with NPCs
    if (event.code === 'KeyF') {
      this.tryInteractWithNPC();
    }

    // E key to pick up loot
    if (event.code === 'KeyE') {
      this.tryPickupLoot();
    }

    // Number keys 1-6 for action bar
    if (event.code >= 'Digit1' && event.code <= 'Digit6') {
      const slotIndex = parseInt(event.code.replace('Digit', '')) - 1;
      this.useSkillSlot(slotIndex);
    }

    // C key to toggle character panel (inventory system has its own key handler)
    if (event.code === 'KeyC') {
      // Character panel - inventory system handles I/B keys
      this.hud.showNotification('Character panel - coming soon', '#888888');
    }

    // U key to open Enhancement UI (MU Online style)
    if (event.code === 'KeyU') {
      if (this.enhancementUI.isUIOpen()) {
        this.enhancementUI.hide();
        this.audioSystem.play('windowClose');
      } else {
        this.enhancementUI.show();
        this.audioSystem.play('windowOpen');
      }
    }

    // Escape to interrupt casting
    if (event.code === 'Escape') {
      if (this.skillSystem.isCastingSkill()) {
        this.skillSystem.interruptCast();
      } else if (this.enhancementUI.isUIOpen()) {
        this.enhancementUI.hide();
        this.audioSystem.play('windowClose');
      } else if (this.inventorySystem.isInventoryOpen()) {
        this.inventorySystem.close();
        this.audioSystem.play('windowClose');
      } else if (this.dialogUI.isDialogOpen()) {
        this.dialogUI.close();
      }
    }

  }

  private targetNearestEnemy(): void {
    const playerPos = this.player.getPosition();
    let nearestEnemy: Enemy | null = null;
    let nearestDist = Infinity;

    for (const enemy of this.enemies) {
      if (!enemy.isAlive()) continue;
      const dist = enemy.mesh.position.distanceTo(playerPos);
      if (dist < nearestDist && dist < 30) {
        nearestDist = dist;
        nearestEnemy = enemy;
      }
    }

    if (nearestEnemy) {
      this.targetingSystem.setTarget(nearestEnemy);
      this.hud.showTarget(nearestEnemy.name, nearestEnemy.level, nearestEnemy.health, nearestEnemy.maxHealth);
      this.audioSystem.play('targetSelect');
    }
  }

  private tryInteractWithNPC(): void {
    if (this.dialogUI.isDialogOpen()) return;

    const playerPos = this.player.getPosition();

    for (const npc of this.npcs) {
      if (npc.isInRange(playerPos)) {
        this.interactWithNPC(npc);
        break;
      }
    }
  }

  private interactWithNPC(npc: NPC): void {
    if (this.dialogUI.isDialogOpen()) return;

    const playerPos = this.player.getPositionRef();

    // Check if player is in range
    if (!npc.isInRange(playerPos)) {
      this.hud.showNotification('Too far away to interact', '#ff8800');
      return;
    }

    npc.facePosition(playerPos);
    this.audioSystem.play('npcGreet');

    // Build NPC info for dialog
    const npcInfo: NPCInfo = {
      id: npc.id,
      name: npc.name,
      type: npc.type as NPCInfo['type'],
      greeting: npc.type === 'vendor' ? VENDORS[npc.id]?.greeting : undefined,
    };

    // Get quests for this NPC
    const availableQuests = this.questSystem.getAvailableQuests(npc.id);
    const turnInQuests = this.questSystem.getTurnInQuests(npc.id);

    // Show unified dialog (works for all NPC types now)
    this.dialogUI.showNPCDialog(
      npc.name,
      availableQuests,
      turnInQuests,
      (questId) => this.questSystem.acceptQuest(questId),
      (questId) => {
        const result = this.questSystem.turnInQuest(questId);
        if (result.success && result.rewards) {
          // Give rewards
          if (result.rewards.gold) {
            this.inventorySystem.addGold(result.rewards.gold);
            this.floatingText.showGold(result.rewards.gold, playerPos);
          }
          if (result.rewards.experience) {
            this.floatingText.showXP(result.rewards.experience, playerPos);
          }
          this.hud.showNotification(`Quest completed: ${result.rewards.experience} XP, ${result.rewards.gold} gold`, '#00ff00');
        }
      },
      npcInfo
    );
  }

  private tryPickupLoot(): void {
    const playerPos = this.player.getPosition();

    for (let i = this.lootDrops.length - 1; i >= 0; i--) {
      const loot = this.lootDrops[i];

      if (loot.isInRange(playerPos)) {
        const added = this.inventorySystem.addItem(loot.itemId, loot.quantity);

        if (added) {
          this.hud.showPickupNotification(loot.item.icon, loot.item.name, loot.quantity);
          this.audioSystem.play('itemPickup');

          // Update collect quest progress
          const newCount = this.inventorySystem.getItemCount(loot.itemId);
          this.questSystem.updateCollectProgress(loot.itemId, newCount);

          loot.destroy();
          this.lootDrops.splice(i, 1);
        } else {
          this.hud.showNotification('Inventory full!', '#ff4444');
          this.audioSystem.play('error');
        }
        break;
      }
    }
  }

  private onResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  public async start(): Promise<void> {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastTime = performance.now();
    this.gameLoop(this.lastTime);

    // Try to connect to multiplayer server
    try {
      await this.networkManager.connect();
      console.log('🌐 Connected to multiplayer server');
    } catch {
      console.log('⚠️ Running in single-player mode (server not available)');
    }

    // Hide loading screen
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
    }

    console.log('🚀 Laika Game Engine started');
    console.log('📋 Controls:');
    console.log('   WASD - Move');
    console.log('   SHIFT - Sprint');
    console.log('   SPACE - Jump');
    console.log('   Right-click + drag - Rotate camera');
    console.log('   Left-click - Target enemy');
    console.log('   TAB - Target nearest enemy');
    console.log('   1-6 - Use skills');
    console.log('   F - Interact with NPC/Vendor');
    console.log('   E - Pick up loot');
    console.log('   I/B - Open inventory');
    console.log('   C - Open character sheet');
    console.log('   M - Toggle minimap');
    console.log('   O - Open settings');
    console.log('   ENTER - Open chat');
    console.log('   ESC - Cancel cast/Close menus/Settings');
    console.log('');
    console.log('⚔️ Warrior Skills: Attack, Heroic Strike, Shield Bash, Battle Cry, Whirlwind, Bandage');
    console.log('🎯 Talk to the Village Elder to get your first quest!');
    console.log('🛒 Visit vendors to buy/sell items!');
  }

  public stop(): void {
    this.isRunning = false;
    console.log('🛑 Laika Game Engine stopped');
  }

  public pause(): void {
    this.isPaused = true;
    console.log('⏸️ Game paused');
  }

  public resume(): void {
    this.isPaused = false;
    this.lastTime = performance.now();
    console.log('▶️ Game resumed');
  }

  public togglePause(): void {
    if (this.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  private gameLoop(currentTime: number): void {
    if (!this.isRunning) return;

    requestAnimationFrame(this.boundGameLoop);

    this.deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    if (this.deltaTime > 0.1) {
      this.deltaTime = 0.1;
    }

    // FPS calculation
    this.frameCount++;
    this.fpsTime += this.deltaTime;
    if (this.fpsTime >= 1) {
      this.currentFPS = Math.round(this.frameCount / this.fpsTime);
      this.frameCount = 0;
      this.fpsTime = 0;

      if (this.fpsElement) {
        this.fpsElement.textContent = `FPS: ${this.currentFPS}`;
      }
    }

    if (!this.isPaused) {
      this.update(this.deltaTime);
    }

    this.render();
  }

  private update(deltaTime: number): void {
    const input = this.inputManager.getState();

    // Don't process movement input if dialog/inventory/chat open
    const uiOpen = this.dialogUI.isDialogOpen() || this.inventorySystem.isInventoryOpen() || this.chatUI.isChatOpen();

    // Camera rotation (when left mouse button held)
    if (!uiOpen && (input.mouseLeftDown || this.inputManager.isPointerLocked())) {
      this.thirdPersonCamera.rotate(input.mouseDeltaX, input.mouseDeltaY);
    }

    // Update player
    if (!uiOpen) {
      this.player.update(deltaTime, input, this.thirdPersonCamera);
    }

    // Update camera
    this.thirdPersonCamera.update(deltaTime);

    // PERF: Update frustum for culling
    this._projScreenMatrix.multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse
    );
    this._frustum.setFromProjectionMatrix(this._projScreenMatrix);

    // Update enemies - use position reference to avoid cloning
    const playerPos = this.player.getPositionRef();
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];

      // PERF: Skip full AI update for enemies outside frustum (but still check death)
      const inFrustum = this._frustum.containsPoint(enemy.mesh.position);
      if (inFrustum || enemy.getState() !== 'idle') {
        // Always update if visible or in combat (chasing/attacking)
        enemy.update(deltaTime, playerPos);
      }

      if (enemy.isDeadAndRemovable()) {
        enemy.destroy();
        this.enemies.splice(i, 1);
      }
    }

    // Update NPCs
    this.npcs.forEach((npc) => npc.update(deltaTime));

    // Check if player arrived at pending NPC interaction target
    if (this.pendingNPCInteraction && !this.dialogUI.isDialogOpen()) {
      const playerPos = this.player.getPositionRef();
      if (this.pendingNPCInteraction.isInRange(playerPos)) {
        const npc = this.pendingNPCInteraction;
        this.pendingNPCInteraction = null;
        this.player.clearMoveTarget();
        this.interactWithNPC(npc);
      }
    }

    // Update remote players
    this.remotePlayers.forEach((remotePlayer) => remotePlayer.update(deltaTime));

    // Send position update to server - use position reference
    if (this.isMultiplayer) {
      const pos = this.player.getPositionRef();
      const rotY = this.player.getRotation();
      this.networkManager.sendPlayerMove(
        { x: pos.x, y: pos.y, z: pos.z },
        { x: 0, y: rotY, z: 0 }
      );
    }

    // Update loot drops
    this.lootDrops.forEach((loot) => loot.update(deltaTime));

    // Remove old loot (5 minute despawn)
    for (let i = this.lootDrops.length - 1; i >= 0; i--) {
      if (this.lootDrops[i].getAge() > GAME_CONFIG.LOOT_DESPAWN_TIME) {
        this.lootDrops[i].destroy();
        this.lootDrops.splice(i, 1);
      }
    }

    // Update targeting system
    this.targetingSystem.update();

    // Update combat system
    this.combatSystem.update();

    // Update skill system
    this.skillSystem.update(deltaTime);

    // Update HUD action bar cooldowns
    this.hud.updateActionBarCooldowns(
      (skillId) => this.skillSystem.getCooldownPercent(skillId),
      this.skillSystem.getGlobalCooldownPercent()
    );

    // Update cast bar
    if (this.skillSystem.isCastingSkill()) {
      const skill = this.skillSystem.getCastingSkill();
      const progress = this.skillSystem.getCastProgress();
      if (skill) {
        const remaining = skill.castTime * (1 - progress);
        this.hud.updateCastBar(progress, remaining);
      }
    }

    // Update buff display
    this.hud.updateBuffs(this.skillSystem.getBuffs());

    // Sync HUD with skill system stats
    const stats = this.skillSystem.getPlayerStats();
    this.hud.setHealth(stats.health);
    this.hud.setMana(stats.mana);

    // Update target frame if we have a target
    const target = this.targetingSystem.getTarget();
    if (target && target.isAlive()) {
      this.hud.updateTargetHealth(target.health, target.maxHealth);
    } else if (target && !target.isAlive()) {
      this.targetingSystem.clearTarget();
      this.hud.hideTarget();
    }

    // Update floating text
    this.floatingText.update(deltaTime);

    // Update nameplates
    this.nameplateSystem.setTarget(target);
    this.nameplateSystem.update();

    // Update new performance systems
    this.updatePerformanceSystems(deltaTime, playerPos);

    // Update minimap
    this.minimap.updatePlayerPosition(playerPos.x, playerPos.z, this.player.getRotation());
    this.updateMinimapMarkers();

    // Auto-attack system
    this.updateAutoAttack();

    // Reset input deltas
    this.inputManager.resetDeltas();
  }

  private updatePerformanceSystems(deltaTime: number, playerPos: THREE.Vector3): void {
    // Update particle system
    this.particleSystem.update(deltaTime);

    // Update spatial tree with entity positions
    this.updateSpatialTree();

    // Update combo system timeout check
    this.comboSystem.update();

    // Update LOD system
    this.lodSystem.update();

    // Update world chunks based on player position
    this.worldChunkManager.updatePlayerPosition(playerPos.x, playerPos.z);

    // Update optimized nameplates
    this.updateOptimizedNameplates();
  }

  private updateSpatialTree(): void {
    // Update enemies in spatial tree
    for (const enemy of this.enemies) {
      if (enemy.isAlive()) {
        const pos = enemy.mesh.position;
        const entity: SpatialEntity = {
          id: enemy.id,
          position: pos,
          radius: 1,
        };
        this.spatialTree.update(entity);
      }
    }

    // Update player in spatial tree
    const playerPos = this.player.getPositionRef();
    this.spatialTree.update({
      id: 'player',
      position: playerPos,
      radius: 0.5,
    });
  }

  private updateOptimizedNameplates(): void {
    const target = this.targetingSystem.getTarget();

    // Update enemy nameplates
    for (const enemy of this.enemies) {
      this.optimizedNameplates.updateEntity(enemy.id, {
        position: enemy.mesh.position,
        health: enemy.health,
        maxHealth: enemy.maxHealth,
        isTargeted: target?.id === enemy.id,
        isDead: !enemy.isAlive(),
      });
    }

    // Update nameplate rendering
    this.optimizedNameplates.update();
  }

  // Get nearby enemies using spatial tree (much faster than iterating all)
  private getNearbyEnemies(position: THREE.Vector3, radius: number): Enemy[] {
    const nearbyEntities = this.spatialTree.queryRadius(position, radius);
    return nearbyEntities
      .filter(e => e.id !== 'player' && e.id.startsWith('enemy_'))
      .map(e => this.enemies.find(enemy => enemy.id === e.id))
      .filter((e): e is Enemy => e !== undefined && e.isAlive());
  }

  private updateMinimapMarkers(): void {
    const markers = [];

    // Add enemies to minimap
    for (const enemy of this.enemies) {
      if (enemy.isAlive()) {
        const pos = enemy.getPosition();
        markers.push({
          id: enemy.id,
          type: 'enemy' as const,
          x: pos.x,
          z: pos.z,
          name: enemy.name,
        });
      }
    }

    // Add NPCs to minimap
    for (const npc of this.npcs) {
      const pos = (npc as any).mesh?.position;
      if (pos) {
        markers.push({
          id: (npc as any).id || 'npc',
          type: ((npc as any).type === 'vendor' ? 'vendor' : 'npc') as 'vendor' | 'npc',
          x: pos.x,
          z: pos.z,
          name: (npc as any).name,
        });
      }
    }

    this.minimap.setMarkers(markers);
    this.minimap.render();
  }

  private updateAutoAttack(): void {
    if (!this.autoAttackEnabled) return;

    const target = this.targetingSystem.getTarget();
    if (!target || !target.isAlive()) {
      this.autoAttackEnabled = false;
      return;
    }

    const now = Date.now();
    if (now - this.lastAutoAttackTime < this.autoAttackInterval) return;

    // Check range
    const playerPos = this.player.getPosition();
    const targetPos = target.getPosition();
    const distance = playerPos.distanceTo(targetPos);

    if (distance <= GAME_CONFIG.ATTACK_RANGE_MELEE) {
      const result = this.combatSystem.attack(target);
      if (result.hit) {
        this.lastAutoAttackTime = now;
        this.combatLog.logDamageDealt(target.name, result.damage, false);

        if (result.killed) {
          this.combatLog.logKill(target.name);
          this.onEnemyKilled(target);
        }
      }
    }
  }

  public startAutoAttack(): void {
    const target = this.targetingSystem.getTarget();
    if (target && target.isAlive()) {
      this.autoAttackEnabled = true;
      this.lastAutoAttackTime = 0; // Attack immediately
    }
  }

  public stopAutoAttack(): void {
    this.autoAttackEnabled = false;
  }


  private render(): void {
    // PERF: Update shadows at lower frequency
    this.shadowUpdateCounter++;
    if (this.shadowUpdateCounter >= this.SHADOW_UPDATE_INTERVAL) {
      this.shadowUpdateCounter = 0;
      this.renderer.shadowMap.needsUpdate = true;
    }

    this.renderer.render(this.scene, this.camera);
  }

  // Getters
  public getScene(): THREE.Scene {
    return this.scene;
  }

  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  public getPlayer(): Player {
    return this.player;
  }

  public getEnemies(): Enemy[] {
    return this.enemies;
  }

  // PERF: Auto-detect quality level based on device capabilities
  private detectQuality(): 'low' | 'medium' | 'high' {
    // Check for mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    // Check hardware concurrency (CPU cores)
    const cores = navigator.hardwareConcurrency || 2;

    // Check device memory (in GB, Chrome only)
    const memory = (navigator as any).deviceMemory || 4;

    // Check WebGL capabilities
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    let maxTextureSize = 2048;
    if (gl) {
      maxTextureSize = (gl as WebGLRenderingContext).getParameter(
        (gl as WebGLRenderingContext).MAX_TEXTURE_SIZE
      );
    }

    // Score-based detection
    let score = 0;

    // Mobile penalty
    if (isMobile) {
      score -= 3;
    }

    // CPU cores
    if (cores >= 8) score += 2;
    else if (cores >= 4) score += 1;
    else score -= 1;

    // Memory
    if (memory >= 8) score += 2;
    else if (memory >= 4) score += 1;
    else score -= 1;

    // GPU texture size capability
    if (maxTextureSize >= 8192) score += 2;
    else if (maxTextureSize >= 4096) score += 1;

    // Device pixel ratio (higher = more GPU work needed)
    if (window.devicePixelRatio > 2) score -= 1;

    // Return quality level based on score
    if (score >= 4) return 'high';
    if (score >= 1) return 'medium';
    return 'low';
  }
}
